import * as THREE from 'three';
import RAPIER from '@dimforge/rapier3d-compat';
import type { City, Facility, Place } from '../world/city';
import type { Vec } from '../destruction/structure';
import type { PhysicsCity } from '../physics/world';
import type { Population, Person, TrafficVehicle } from './population';
import type { FireSystem } from './fire';
import { Reservations } from './reservations';
import type { RoutePoint } from './routes';
import { localFootRoute } from './local-foot-route';
import { IncidentAssistance } from './incident-assistance';

type Role = 'fire' | 'police' | 'medical' | 'tow';
interface CrewPath {
  crew: Person;
  incident?: string;
  route: number[];
  cursor: number;
  planning?: Generator<void, number[]>;
  routeRetry?: number;
  routePoint?: THREE.Vector3;
  accessSince?: number;
  footProgress?: { goal: Vec; distance: number; at: number };
  footBypass?: Vec[];
  footExit?: Vec[];
  planFailure?: { starts: number; ends: number; reason: string };
  bypassRetry?: number;
  nextFootProbe?: number;
  directGoal?: THREE.Vector3;
}
interface SupportCrew extends CrewPath {
  state: 'aboard' | 'deploying' | 'working' | 'returning';
  goal?: THREE.Vector3;
  goalSubject?: THREE.Vector3;
  workRetry?: number;
  work: number;
}
export interface CityIncident {
  airObservedUntil?: number;
  majorRadius?: number;
  id: string;
  point: THREE.Vector3;
  fires: Set<string>;
  people: Set<string>;
  wrecks: Set<string>;
  severity: number;
  state: 'active' | 'waiting' | 'recovering' | 'complete';
  reason: string;
  quiet: number;
  age: number;
}
interface Unit extends CrewPath {
  airHandoffRequested?: boolean;
  patientRoutePosition?: THREE.Vector3;
  patientApproachRetry?: number;
  rejectedPatientApproaches?: THREE.Vector3[];
  driverRetained?: boolean;
  pickupWaiting?: boolean;
  rendezvousMoving?: boolean;
  rendezvousProbe?: number;
  rendezvousStarted?: number;
  rendezvousRetry?: number;
  hospitalRetry?: number;
  hospitalBay?: string;
  admissionWait?: { point: THREE.Vector3; since: number };
  avoidStage?: THREE.Vector3;
  duty?: Place;
  dutyChecks: number;
  dutyStep: number;
  dutyStarted: boolean;
  id: string;
  role: Role;
  facility: Facility;
  car: TrafficVehicle;
  crew: Person;
  additional: SupportCrew[];
  boardingFor?: string;
  egressAt: number;
  footFallback?: boolean;
  recoverable?: 'crew' | 'route';
  recoveryRetry?: number;
  handoffExit?: THREE.Vector3;
  incident?: string;
  state:
    | 'ready'
    | 'muster'
    | 'outbound'
    | 'approaching'
    | 'stabilising'
    | 'carrying'
    | 'suppression'
    | 'control'
    | 'hooking'
    | 'loading'
    | 'unloading'
    | 'transport'
    | 'admitting'
    | 'returning'
    | 'crew-return'
    | 'turnaround'
    | 'unavailable';
  timer: number;
  water: number;
  patient?: string;
  wreck?: string;
  target?: THREE.Vector3;
  route: number[];
  cursor: number;
  destination?: Facility;
  bed?: number;
  attempts: number;
  routeGoal?: RoutePoint;
  loadFrom?: THREE.Vector3;
  loadTo?: THREE.Vector3;
  unloadDoor?: THREE.Vector3;
}
interface Care {
  phase: 'entering' | 'resting' | 'leaving';
  elapsed: number;
  from: THREE.Vector3;
  yaw: number;
  tilt: number;
  unit: string;
  person: Person;
  facility: Facility;
  bed: number;
  remaining: number;
  incident: string;
}
/** Finite shared dispatch, with original actor/body identities through treatment and towing. */
export class Services {
  readonly incidents = new Map<string, CityIncident>();
  readonly units: Unit[] = [];
  readonly log: { time: number; incident: string; unit: string; action: string; entity?: string }[] = [];
  readonly reservations: Reservations;
  readonly assistance = new IncidentAssistance(this);
  private assistingPaths = new Map<string, CrewPath>();
  readonly fieldStabilised = new Set<string>();
  private care: Care[] = [];
  private serial = 0;
  private age = 0;
  private clock = 0;
  private epoch = 0;
  admissions = 0;
  recovered = 0;
  towed = 0;
  waterApplied = 0;
  private dispositions = new Set<string>();
  private airAccess = new Map<string, number>();
  private availability = new Map<
    number,
    { version: number; value: boolean; support?: number; supportVersion?: number }
  >();
  private crewPlans = new Set<CrewPath>();
  private parkedNodes?: { at: number; nodes: Set<number> };
  private parkedFootprints = new WeakMap<
    TrafficVehicle,
    { x: number; y: number; z: number; yaw: number; nodes: number[] }
  >();
  private peopleById = new Map<string, Person>();
  private liveFires: ReturnType<FireSystem['incidents']> = [];
  private framePlanningMs?: number;
  beginFrame(budgetMs = 0.8): void {
    this.framePlanningMs = budgetMs;
  }
  private hoseData = new Float32Array(256 * 6);
  private hose = new THREE.LineSegments(
    new THREE.BufferGeometry(),
    new THREE.LineBasicMaterial({ color: 0x96cfd3, transparent: true, opacity: 0.7 }),
  );
  constructor(
    readonly city: City,
    readonly physics: PhysicsCity,
    readonly population: Population,
    readonly fire: FireSystem,
  ) {
    this.reservations = new Reservations(() => physics.life.epoch);
    this.hose.geometry.setAttribute(
      'position',
      new THREE.BufferAttribute(this.hoseData, 3).setUsage(THREE.DynamicDrawUsage),
    );
    this.hose.frustumCulled = false;
    physics.scene.add(this.hose);
    this.reset();
  }
  reset(): void {
    for (const step of this.resetSteps()) void step;
  }
  *resetSteps(): Generator<void> {
    this.clear();
    this.epoch = this.physics.life.epoch;
    const sequence = new Map<Role, number>();
    for (const facility of this.city.facilities ?? [])
      for (let i = 0; i < facility.bays.length; i++) {
        const role: Role =
          facility.kind === 'hospital' ? 'medical' : facility.kind === 'depot' ? 'tow' : facility.kind;
        const number = sequence.get(role) ?? 0;
        sequence.set(role, number + 1);
        const car = this.population.createCar(
          `service:${role}:${number}`,
          role === 'medical' ? 'ambulance' : role,
          facility.bays[i],
          facility.node,
        );
        car.controlled = true;
        car.state = 'station';
        car.home.copy(car.p);
        car.station = facility.node;
        car.name = `${role} unit ${number + 1}`;
        car.color =
          role === 'fire' ? 0xc87762 : role === 'medical' ? 0xffffff : role === 'tow' ? 0xd9b67b : 0xb4cbd4;
        const crew = this.population.createCrew(`crew:${role}:${number}`, car.p);
        crew.hidden = true;
        crew.name = `${role} crew ${number + 1}`;
        const additional: SupportCrew[] = [];
        for (let member = 1; member < (this.city.regional ? 5 : 1); member++) {
          const person = this.population.createCrew(`crew:${role}:${number}:${member}`, car.p);
          person.name = `${role} unit ${number + 1} · ${['driver', 'operator', 'assistant', 'technician'][member - 1]}`;
          person.hidden = true;
          person.color = crew.color;
          additional.push({ crew: person, state: 'aboard', route: [], cursor: 0, work: 0 });
          yield;
        }
        this.units.push({
          id: car.id,
          duty: facility.duties?.[i],
          dutyChecks: 0,
          dutyStep: 0,
          dutyStarted: false,
          role,
          facility,
          car,
          crew,
          additional,
          egressAt: 0,
          state: 'ready',
          timer: 0,
          water: 1,
          route: [],
          cursor: 0,
          attempts: 0,
        });
        yield;
      }
  }
  clear(): void {
    this.assistance.clear();
    this.assistingPaths.clear();
    this.fieldStabilised.clear();
    this.incidents.clear();
    this.units.length = 0;
    this.care = [];
    this.log.length = 0;
    this.reservations.clear();
    this.dispositions.clear();
    this.airAccess.clear();
    this.availability.clear();
    for (const path of this.crewPlans) path.planning?.return([]);
    this.crewPlans.clear();
    this.parkedNodes = undefined;
    this.parkedFootprints = new WeakMap();
    this.peopleById.clear();
    this.serial =
      this.age =
      this.clock =
      this.admissions =
      this.recovered =
      this.towed =
      this.waterApplied =
        0;
  }
  invalidate(id: number): void {
    this.availability.delete(id);
  }
  private fixedCell(id: number): boolean {
    const owner = this.physics.cellOwner.get(id);
    return (
      owner !== undefined && !!(this.physics.clusters.get(owner) ?? this.physics.dormant.get(owner))?.anchored
    );
  }
  available(f: Facility): boolean {
    const cached = this.availability.get(f.buildingId),
      version = this.physics.buildingSupportVersion(f.buildingId);
    if (
      cached &&
      cached.version === version &&
      (cached.support === undefined ||
        this.physics.staticSupportVersion(cached.support) === cached.supportVersion)
    )
      return cached.value;
    const building = this.physics.buildings.find((b) => b.id === f.buildingId);
    if (!building) return false;
    let supported = 0;
    for (const cell of this.physics.buildingCells(f.buildingId)) {
      const owner = this.physics.cellOwner.get(cell.id),
        cluster =
          owner === undefined
            ? undefined
            : (this.physics.clusters.get(owner) ?? this.physics.dormant.get(owner));
      if (cluster?.anchored) supported++;
    }
    const floor = this.physics.ground(f.bays[0], 0.25, 0.35),
      value = supported > building.cells.length * 0.4 && !!floor;
    this.availability.set(f.buildingId, {
      version,
      value,
      support: floor?.owner,
      supportVersion: floor && this.physics.staticSupportVersion(floor.owner),
    });
    return value;
  }
  private record(unit: Unit, action: string, entity?: string): void {
    this.log.push({ time: this.clock, incident: unit.incident ?? '', unit: unit.id, action, entity });
    if (this.log.length > 1024) this.log.shift();
  }
  private person(id: string | undefined): Person | undefined {
    if (!id) return;
    let p = this.peopleById.get(id);
    if (!p) {
      p = this.population.people.find((p) => p.id === id);
      if (p) this.peopleById.set(id, p);
    }
    return p;
  }
  private incidentFor(point: Vec, source: string): CityIncident {
    const known = [...this.incidents.values()].find(
      (i) => i.fires.has(source) || i.people.has(source) || i.wrecks.has(source),
    );
    if (known) return known;
    const nearby = [...this.incidents.values()].find(
      (i) =>
        i.state !== 'complete' &&
        i.point.distanceTo(new THREE.Vector3(point.x, point.y, point.z)) < (i.majorRadius ?? 20),
    );
    if (nearby) return nearby;
    const incident: CityIncident = {
      id: `incident:${this.epoch}:${++this.serial}`,
      point: new THREE.Vector3(point.x, point.y, point.z),
      fires: new Set(),
      people: new Set(),
      wrecks: new Set(),
      severity: 1,
      state: 'active',
      reason: 'Assessing access and available roles',
      quiet: 0,
      age: 0,
    };
    this.incidents.set(incident.id, incident);
    return incident;
  }
  reportCollapse(point: Vec, radius: number): void {
    if (!this.city.regional || radius < 25) return;
    const incident = this.incidentFor(point, 'structural-collapse');
    incident.majorRadius = Math.min(140, Math.max(50, radius + 25));
    incident.reason = 'Major structural collapse; coordinating regional response';
  }
  private scan(): void {
    const heat = this.fire.incidents();
    for (const i of this.incidents.values()) i.fires.clear();
    for (const f of heat) this.incidentFor(f.point, f.id).fires.add(f.id);
    for (const p of this.population.people)
      if (p.state === 'incapacitated' && !p.hidden && !p.fatal) {
        this.dispositions.delete(p.id);
        this.incidentFor(p.p, p.id).people.add(p.id);
      }
    for (const car of this.population.cars)
      if (car.state === 'wreck' && !this.dispositions.has(car.id))
        this.incidentFor(car.p, car.id).wrecks.add(car.id);
    this.population.network.closures.clear();
    for (const i of this.incidents.values()) {
      i.age += 0.5;
      for (const id of i.people) if (this.dispositions.has(id)) i.people.delete(id);
      for (const id of i.wrecks) if (this.dispositions.has(id)) i.wrecks.delete(id);
      i.severity = Math.min(
        5,
        1 + Math.floor(i.fires.size / 3) + Math.floor(i.people.size / 4) + (i.wrecks.size > 2 ? 1 : 0),
      );
      if (i.majorRadius) i.severity = Math.max(i.severity, 4);
      if (!i.fires.size && !i.people.size && !i.wrecks.size) {
        i.quiet += 0.5;
        if (i.quiet > 5) {
          i.state = 'complete';
          i.reason = 'Hazards cleared; transported identities retained';
        }
        if (i.quiet > 120 && !this.units.some((u) => u.incident === i.id)) this.incidents.delete(i.id);
        continue;
      }
      i.quiet = 0;
      i.state = i.fires.size ? 'active' : 'recovering';
      const required: Role[] = [
        ...(i.fires.size ? ['fire' as Role] : []),
        ...(i.people.size ? ['medical' as Role] : []),
        ...((!i.fires.size && i.wrecks.size) || (this.city.regional && i.severity >= 4)
          ? ['tow' as Role]
          : []),
        ...(i.fires.size || i.people.size || i.wrecks.size ? ['police' as Role] : []),
      ];
      for (const role of required) {
        const desired =
            this.city.regional && i.severity >= 4
              ? { fire: 12, medical: 10, police: 8, tow: 4 }[role]
              : role === 'fire' && i.severity >= 3
                ? 2
                : 1,
          assigned = this.units.filter(
            (u) =>
              u.incident === i.id &&
              u.role === role &&
              !['unavailable', 'returning', 'crew-return', 'turnaround', 'ready'].includes(u.state),
          ).length;
        if (assigned >= desired) continue;
        const choices = this.units
          .filter(
            (u) =>
              u.role === role &&
              u.state === 'ready' &&
              (role !== 'fire' || u.water > 0.1) &&
              !['incapacitated', 'falling', 'flee'].includes(u.crew.state) &&
              this.available(u.facility),
          )
          .sort((a, b) => a.car.p.distanceTo(i.point) - b.car.p.distanceTo(i.point));
        let sent = false;
        for (const unit of choices) {
          if (this.dispatch(unit, i)) {
            sent = true;
            break;
          }
        }
        if (!sent) {
          i.state = 'waiting';
          i.reason = `Waiting for reachable ${role} capacity`;
        }
      }
      for (const unit of this.units)
        if (unit.incident === i.id && unit.state === 'control') {
          const a = this.population.network.approach(i.point, true);
          if (a) this.population.network.closures.add(a.edge.id);
          for (const m of unit.additional)
            if (!m.crew.hidden && m.state === 'working') {
              const approach = this.population.network.approach(m.crew.p, true);
              if (approach) this.population.network.closures.add(approach.edge.id);
            }
        }
      if ((i.airObservedUntil ?? 0) > this.clock) {
        // Aerial assessment confirms a wider exclusion around a major incident.
        for (const edge of this.city.roadGraph!.edges) {
          const a = this.city.roadGraph!.nodes[edge.from],
            b = this.city.roadGraph!.nodes[edge.to];
          const dx = b.x - a.x,
            dz = b.z - a.z,
            t = Math.max(
              0,
              Math.min(1, ((i.point.x - a.x) * dx + (i.point.z - a.z) * dz) / (dx * dx + dz * dz)),
            );
          if (Math.hypot(i.point.x - a.x - dx * t, i.point.z - a.z - dz * t) < 28)
            this.population.network.closures.add(edge.id);
        }
      }
    }
  }
  observeIncident(id: string): void {
    const i = this.incidents.get(id);
    if (i && i.state !== 'complete') i.airObservedUntil = this.clock + 15;
  }
  airRescueRequest(person: Person): string | undefined {
    if (
      person.state !== 'incapacitated' ||
      person.fatal ||
      person.controlled ||
      this.reservations.holders(`patient:${person.id}`, this.clock).length
    )
      return;
    if (
      this.city.helipads?.some(
        (pad) =>
          person.p.distanceTo(new THREE.Vector3(pad.handoff.x, pad.handoff.y, pad.handoff.z)) < 12 &&
          this.available(pad.facility) &&
          this.population.civil?.network.available(pad.handoff),
      )
    )
      return;
    if ((this.airAccess.get(person.id) ?? 0) > this.clock)
      return 'Ground crew confirmed an inaccessible patient; air extraction requested';
    if (person.p.y > 4.5) return 'Exposed elevated casualty requires extraction';
    const incident = [...this.incidents.values()].find((i) => i.people.has(person.id));
    if (!incident || incident.age < 45) return;
    // A patient already at a working hospital handoff needs the ground crew,
    // not another flight back to the same place.
    if (
      this.city.facilities?.some(
        (f) =>
          f.kind === 'hospital' &&
          person.p.distanceTo(new THREE.Vector3(f.entry.x, f.entry.y, f.entry.z)) < 35 &&
          this.available(f),
      )
    )
      return;
    const assigned = this.units.filter(
      (u) => u.role === 'medical' && u.incident === incident.id && u.state !== 'unavailable',
    );
    if (!assigned.length) return 'Ground medical capacity unavailable for waiting casualty';
    if (
      assigned.every(
        (u) =>
          ['transport', 'admitting', 'returning', 'turnaround'].includes(u.state) ||
          (u.state === 'outbound' && u.attempts >= 3),
      )
    )
      return 'Ground medical access delayed; air extraction can reach the casualty';
  }
  relocateCasualty(person: Person): void {
    this.airAccess.delete(person.id);
    for (const i of this.incidents.values()) i.people.delete(person.id);
    this.incidentFor(person.p, person.id).people.add(person.id);
  }
  private routeVehicle(u: Unit, destination: Vec, facility?: Facility): boolean {
    u.unloadDoor = undefined;
    u.car.parkingYaw = undefined;
    const approach = this.population.network.approach(facility?.entry ?? destination, true);
    if (!approach) return false;
    // A garage departure first traverses its entrance. Projecting the bay onto
    // the road instead creates a backwards lateral jog after the entrance,
    // which can steer a long recovery truck into the wreck it came to collect.
    const leavingStation = u.state === 'ready' || u.state === 'turnaround';
    const routes = [approach.edge.from, approach.edge.to]
      .map((node) =>
        this.population.plannedRoute(
          u.car,
          approach.point,
          node,
          true,
          leavingStation ? u.facility.entry : u.car.p,
        ),
      )
      .filter((path) => path.length);
    const distance = (path: RoutePoint[]) =>
      path.reduce((sum, p, i) => {
        const before = i ? path[i - 1] : u.car.p;
        return sum + Math.hypot(p.x - before.x, p.z - before.z);
      }, 0);
    const path = routes.sort((a, b) => distance(a) - distance(b))[0] ?? [];
    if (!path.length) return false;
    if (leavingStation) path.unshift({ ...u.facility.entry, maxSpeed: 1.6 });
    if (facility) {
      path.push({ ...facility.entry });
      if (facility.kind === 'hospital' && u.patient) {
        const building = this.city.buildings.find((b) => b.id === facility.buildingId)!;
        path.push({ x: destination.x, y: destination.y, z: building.z - 4.3, maxSpeed: 1.6, hold: 0.5 });
        path.push({ ...destination, reverse: true, maxSpeed: 1.6 });
      } else path.push({ ...destination });
      // Garage bays face their public entrance. The final manoeuvre backs an
      // ambulance toward the ward so its rear unloading door opens into the aisle.
      if (facility.kind === 'hospital' && u.patient) u.car.parkingYaw = 0;
    } else if (Math.hypot(destination.x - approach.point.x, destination.z - approach.point.z) > 1)
      path.push({ ...destination });
    u.car.plan = path;
    u.routeGoal = { ...destination };
    u.car.state = 'enroute';
    u.car.incident = u.incident;
    u.car.targetSpeed = u.car.speed;
    return true;
  }
  private dispatch(u: Unit, i: CityIncident): boolean {
    if (
      this.units.some(
        (other) =>
          other !== u &&
          other.facility === u.facility &&
          other.state === 'outbound' &&
          other.car.p.distanceTo(
            new THREE.Vector3(u.facility.entry.x, u.facility.entry.y, u.facility.entry.z),
          ) < 14,
      )
    ) {
      i.reason = 'Waiting for the station access lane';
      return false;
    }
    const major = this.city.regional && i.severity >= 4;
    const approaches = this.population.network
      .approaches(i.point, true, u.car.length, major ? 18 : 6)
      .filter((a) => Math.hypot(a.point.x - i.point.x, a.point.z - i.point.z) <= (major ? 145 : 75));
    if (!approaches.length) {
      i.reason = 'No safe road access';
      return false;
    }
    let stage: Vec | undefined,
      stageYaw = 0;
    const previousIncident = u.incident;
    for (const a of approaches) {
      const edge = a.edge,
        from = this.population.network.nodes.get(edge.from)!,
        to = this.population.network.nodes.get(edge.to)!,
        length = Math.hypot(to.x - from.x, to.z - from.z);
      const origin =
        ((a.point.x - from.x) * (to.x - from.x) + (a.point.z - from.z) * (to.z - from.z)) / (length * length);
      const side = new THREE.Vector3(-(to.z - from.z) / length, 0, (to.x - from.x) / length);
      for (const offset of major
        ? [0, -10, 10, -20, 20, -30, 30, -40, 40, -50, 50]
        : [0, -10, 10, -20, 20, -30, 30]) {
        const t = origin + offset / length;
        if (t < 0.12 || t > 0.88) continue;
        const lane = this.population.network.waypoint(edge, edge.from, t);
        // Try a real off-road work area first, then a supported kerbside stop.
        // The previous single far-side offset often landed inside city buildings.
        for (const lateral of [
          edge.width / 2 + 5.5,
          edge.width / 2 + 1.4,
          Math.max(0.6, edge.width / 2 - u.car.width * 0.6),
        ]) {
          for (const sign of [1, -1]) {
            const candidate = new THREE.Vector3(lane.x, lane.y, lane.z).addScaledVector(side, sign * lateral);
            if (u.avoidStage && candidate.distanceTo(u.avoidStage) < 12) continue;
            const support = this.physics.ground(candidate, 0.35, 0.5);
            if (!support || this.physics.blocked(candidate.x, candidate.z, u.car.width * 0.7, support.y + 1))
              continue;
            candidate.y = support.y;
            if (candidate.distanceTo(i.point) < u.car.length / 2 + 3) continue;
            const yaw = Math.atan2(-(to.x - from.x), -(to.z - from.z)),
              shape = u.car.collider.halfExtents()!,
              local = u.car.collider.translationWrtParent()!;
            if (
              !this.physics.occupiedBoxClear(
                { x: candidate.x, y: candidate.y + local.y, z: candidate.z },
                shape,
                new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), yaw),
                { x: 0, y: 0, z: 0 },
                u.car.body,
              )
            )
              continue;
            if (
              [-0.4, 0, 0.4].some((f) =>
                this.physics.blocked(
                  candidate.x + ((to.x - from.x) / length) * u.car.length * f,
                  candidate.z + ((to.z - from.z) / length) * u.car.length * f,
                  u.car.width * 0.65,
                  candidate.y + 1,
                ),
              )
            )
              continue;
            // Compare the final positions, not the unclamped offset. A full staging
            // area must make another responder wait rather than block the first one.
            if (
              this.units.some(
                (other) =>
                  other !== u &&
                  other.incident &&
                  other.target &&
                  !['returning', 'crew-return', 'turnaround', 'ready', 'unavailable'].includes(other.state) &&
                  other.target.distanceTo(candidate) < 10,
              )
            )
              continue;
            if (
              this.population.cars.some(
                (car) =>
                  car !== u.car &&
                  car.state !== 'driving' &&
                  Math.hypot(car.p.x - candidate.x, car.p.z - candidate.z) <
                    (car.length + u.car.length) / 2 + 2,
              )
            )
              continue;
            stage = { x: candidate.x, y: candidate.y, z: candidate.z };
            u.incident = i.id;
            if (!this.routeVehicle(u, stage)) {
              stage = undefined;
              u.incident = previousIncident;
              continue;
            }
            stageYaw = yaw;
            break;
          }
          if (stage) break;
        }
        if (stage) break;
      }
      if (stage) break;
    }
    if (!stage) {
      i.reason = 'Waiting for an accessible roadside work space';
      return false;
    }
    u.state = u.crew.hidden ? 'outbound' : 'muster';
    if (u.state === 'muster') {
      u.car.state = 'station';
      u.crew.detour = undefined;
      this.crewRoute(u, this.crewDoor(u));
    }
    u.dutyStarted = false;
    u.car.parkingYaw = stageYaw;
    u.target = new THREE.Vector3(stage.x, stage.y, stage.z);
    u.attempts = 0;
    u.footFallback = false;
    this.record(u, 'dispatched');
    return true;
  }
  blocked(car: TrafficVehicle): void {
    const u = this.units.find((u) => u.car === car);
    if (!u) return;
    // A repeatedly successful graph replan used to reset attempts forever,
    // even when the vehicle never got past the same wreck. Foot access takes
    // precedence after actual lack of progress near the incident.
    const incident = u.incident ? this.incidents.get(u.incident) : undefined;
    if (u.state === 'outbound' && incident && car.p.distanceTo(incident.point) < 220) {
      const prior = u.admissionWait;
      if (!prior || prior.point.distanceTo(car.p) > 3)
        u.admissionWait = { point: car.p.clone(), since: this.clock };
      else if (this.clock - prior.since > 4 && this.unloadCrew(u)) {
        car.plan = [];
        car.actualSpeed = car.targetSpeed = 0;
        car.state = 'deployed';
        u.footFallback = true;
        u.driverRetained = !!u.additional[0]?.crew.hidden;
        this.record(u, 'No progress past road obstruction; crew disembarking for supported foot access');
        return;
      }
    }
    if (u.role === 'medical' && u.state === 'transport' && u.destination && car.actualSpeed < 0.05) {
      if (!u.admissionWait || u.admissionWait.point.distanceTo(car.p) > 1)
        u.admissionWait = { point: car.p.clone(), since: this.clock };
      if (
        this.clock - u.admissionWait.since >= 8 &&
        car.p.distanceTo(
          new THREE.Vector3(u.destination.entry.x, u.destination.entry.y, u.destination.entry.z),
        ) < 60 &&
        this.beginAdmission(u)
      ) {
        this.record(
          u,
          'near hospital access blocked; original medic carrying patient from the actual door',
          u.patient,
        );
        return;
      }
    }
    if (car.blockedBy === 'structure' || car.blockedBy.startsWith('road:')) {
      const incident = u.incident ? this.incidents.get(u.incident) : undefined;
      if (u.state === 'outbound' && incident) {
        const prior = u.incident;
        if (this.dispatch(u, incident)) {
          car.think = 0;
          this.record(u, 'replanned a blocked approach from live road geometry');
          return;
        }
        u.incident = prior;
      } else if ((u.state === 'transport' || u.state === 'returning') && u.routeGoal) {
        if (this.routeVehicle(u, u.routeGoal, u.state === 'returning' ? u.facility : u.destination)) {
          car.think = 0;
          this.record(u, 'replanned blocked transport route');
          return;
        }
      }
    }
    const pedestrian = this.population.people.find((p) => p.id === car.blockedBy);
    const crewPath = pedestrian?.controlled
      ? this.units.flatMap((unit) => [unit, ...unit.additional]).find((path) => path.crew === pedestrian)
      : undefined;
    if (crewPath && this.population.requestPedestrianClearance(car, true)) {
      this.cancelCrewRoute(crewPath);
      this.record(u, 'original responder stepping clear of the vehicle path', pedestrian!.id);
      return;
    }
    if (
      pedestrian &&
      !pedestrian.controlled &&
      !['incapacitated', 'falling', 'flee'].includes(pedestrian.state) &&
      !pedestrian.yieldTarget
    ) {
      const next = car.plan[0];
      if (next) {
        const direction = new THREE.Vector3(next.x - car.p.x, 0, next.z - car.p.z).normalize();
        const away =
          pedestrian.p
            .clone()
            .sub(car.p)
            .dot(new THREE.Vector3(-direction.z, 0, direction.x)) >= 0
            ? 1
            : -1;
        for (const sign of [away, -away]) {
          const point = pedestrian.p
            .clone()
            .add(
              new THREE.Vector3(-direction.z, 0, direction.x).multiplyScalar(sign * (car.width / 2 + 1.7)),
            );
          const ground = this.physics.ground(point, 0.35, 0.5);
          if (!ground || !this.population.civil?.network.lineOpen(pedestrian.p, { ...point, y: ground.y }))
            continue;
          point.y = ground.y;
          pedestrian.yieldTarget = point;
          pedestrian.detour = undefined;
          pedestrian.yieldUntil = this.population.civil.time + 6;
          this.record(u, 'requesting pedestrian clearance', pedestrian.id);
          return;
        }
      }
    }
    const other = this.population.cars.find((c) => c.id === car.blockedBy);
    const staged =
      other &&
      this.units.find((unit) => unit.car === other && unit.role === 'police' && unit.state === 'control');
    if (staged && u.incident && staged.incident === u.incident) {
      const incident = this.incidents.get(u.incident);
      if (incident) {
        staged.avoidStage = staged.car.p.clone();
        if (this.dispatch(staged, incident)) {
          this.record(staged, 'boarding and relocating original traffic team to clear service access');
          return;
        }
      }
    }
    if (
      other &&
      other.blockedBy === car.id &&
      (!other.controlled || car.id > other.id) &&
      car.plan.length &&
      !car.plan[0].hold
    ) {
      if ([4, 2.5, 1.25].some((distance) => this.population.reverseForClearance(car, distance))) {
        this.record(u, 'reversing through verified clearance to release the access lane');
        return;
      }
      const next = car.plan[0],
        forward = new THREE.Vector3(next.x - car.p.x, 0, next.z - car.p.z).normalize();
      for (const sign of [1, -1]) {
        const escape = car.p
          .clone()
          .add(new THREE.Vector3(-forward.z * sign, 0, forward.x * sign).multiplyScalar(6))
          .addScaledVector(forward, -4);
        const support = this.physics.ground(escape, 0.35, 0.5);
        if (!support) continue;
        escape.y = support.y;
        let clear = true;
        for (let t = 0.15; t <= 1; t += 0.15) {
          const p = car.p.clone().lerp(escape, t);
          if (
            this.physics.blocked(p.x, p.z, car.width * 0.6, p.y + 1) ||
            this.fire
              .incidents()
              .some((f) => Math.hypot(f.point.x - p.x, f.point.z - p.z) < 12 + car.width) ||
            this.population.cars.some((c) => c !== car && c.p.distanceTo(p) < (c.width + car.width) / 2 + 0.4)
          ) {
            clear = false;
            break;
          }
        }
        if (!clear) continue;
        car.plan.unshift({ x: escape.x, y: escape.y, z: escape.z, hold: 5 });
        car.think = 0;
        this.record(u, 'yielding through a verified clear passing space');
        return;
      }
    }
    u.attempts++;
    if (u.state === 'outbound' && u.attempts >= 2 && u.incident && !u.footFallback) {
      const incident = this.incidents.get(u.incident);
      if (incident && u.car.p.distanceTo(incident.point) < 450 && this.unloadCrew(u)) {
        // Keep the original obstructed vehicle here. Its actual crew proceeds
        // from a clear supported door and later returns with the same patient.
        u.car.plan = [];
        u.car.actualSpeed = u.car.targetSpeed = 0;
        u.car.state = 'deployed';
        u.footFallback = true;
        u.driverRetained = !!u.additional[0]?.crew.hidden;
        this.record(u, 'vehicle blocked; original crew continuing on foot');
      }
    }
    if (u.attempts % 3 === 0) {
      const i = u.incident ? this.incidents.get(u.incident) : undefined;
      if (i) {
        i.state = 'waiting';
        i.reason = `${u.role} route obstructed; retaining assignment and requesting access`;
      }
    }
  }
  private crewRoute(u: CrewPath, point: Vec): boolean {
    this.cancelCrewRoute(u);
    u.route = [];
    u.cursor = 0;
    u.routePoint = new THREE.Vector3(point.x, point.y, point.z);
    u.planning = this.planCrew(u, point);
    this.crewPlans.add(u);
    return true;
  }
  private cancelCrewRoute(u: CrewPath): void {
    u.planning?.return([]);
    u.planning = undefined;
    this.crewPlans.delete(u);
    u.routePoint = undefined;
    u.directGoal = undefined;
    u.footBypass = undefined;
    u.footExit = undefined;
    u.planFailure = undefined;
  }
  private *planCrew(u: CrewPath, point: Vec): Generator<void, number[]> {
    const network = this.population.civil?.network;
    if (!network) return [];
    const start = u.crew.p.clone(),
      move = new THREE.Vector3(point.x, point.y, point.z).sub(start),
      distance = move.length();
    if (distance <= 24 && this.physics.sweepFoot(start, move, 0.24, undefined, 0.65) >= 0.999) {
      // A short unobstructed approach need not touch a coarse street node.
      // Prove the entire corridor's physical support, then retain the normal
      // per-step character sweep for moving traffic and newly damaged floors.
      let y = start.y,
        clear = true;
      const steps = Math.max(1, Math.ceil(distance / 0.7));
      for (let k = 1; k <= steps; k++) {
        const p = start.clone().addScaledVector(move, k / steps);
        p.y = y;
        const floor = this.physics.ground(p, 0.65, 0.7);
        if (!floor || Math.abs(floor.y - y) > 0.65) {
          clear = false;
          break;
        }
        y = floor.y;
        yield;
      }
      if (clear && Math.abs(y - point.y) < 0.7) {
        u.directGoal = new THREE.Vector3(point.x, point.y, point.z);
        return [];
      }
    }
    if (distance <= 18 && Math.abs(point.y - start.y) < 0.7) {
      const bypass = yield* localFootRoute(this.physics, start, point, 0.65);
      if (bypass.length) {
        u.footBypass = bypass;
        u.directGoal = new THREE.Vector3(point.x, point.y, point.z);
        return [];
      }
    }
    // Garage vehicles occupy real space after the static walking graph was
    // built. Plan around their current oriented bodies before walking to a
    // locker, patient or boarding door; local nudges cannot cross a parked van.
    const cached = this.parkedNodes;
    const blocked = cached && this.clock - cached.at < 0.25 ? cached.nodes : new Set<number>();
    if (blocked !== cached?.nodes) {
      let checked = 0;
      for (const car of this.population.cars) {
        if (++checked % 8 === 0) yield;
        if (!['station', 'deployed', 'parked', 'wreck'].includes(car.state)) continue;
        const prior = this.parkedFootprints.get(car);
        if (
          prior &&
          prior.x === car.p.x &&
          prior.y === car.p.y &&
          prior.z === car.p.z &&
          prior.yaw === car.yaw
        ) {
          for (const id of prior.nodes) blocked.add(id);
          continue;
        }
        const occupied: number[] = [];
        const cos = Math.cos(car.yaw),
          sin = Math.sin(car.yaw);
        for (const node of network.near(car.p, car.length + 2)) {
          const dx = node.x - car.p.x,
            dz = node.z - car.p.z;
          if (
            Math.abs(node.y - car.p.y) < 2 &&
            Math.abs(dx * cos - dz * sin) < car.width / 2 + 0.3 &&
            Math.abs(dx * sin + dz * cos) < car.length / 2 + 0.35
          ) {
            blocked.add(node.id);
            occupied.push(node.id);
          }
        }
        this.parkedFootprints.set(car, { x: car.p.x, y: car.p.y, z: car.p.z, yaw: car.yaw, nodes: occupied });
      }
      this.parkedNodes = { at: this.clock, nodes: blocked };
    }
    const physics = this.physics;
    const activeNetwork = network;
    const connectedNode = (id: number, start: boolean) =>
      (network.adjacent.get(id) ?? []).some(
        (link) => (!link.oneWay || (start ? link.a === id : link.b === id)) && network.open(link, this.clock),
      );
    function* nearest(p: Vec, start = false): Generator<void, typeof activeNetwork.nodes> {
      const found: typeof activeNetwork.nodes = [];
      const candidates = activeNetwork
        .near(p, 12)
        .filter((n) => !blocked.has(n.id) && Math.abs(n.y - p.y) < 0.7 && connectedNode(n.id, start))
        .sort((a, b) => Math.hypot(a.x - p.x, a.z - p.z) - Math.hypot(b.x - p.x, b.z - p.z));
      for (const n of candidates) {
        const from = start ? p : n,
          to = start ? n : p;
        if (
          physics.sweepFoot(
            from,
            { x: to.x - from.x, y: to.y - from.y, z: to.z - from.z },
            0.24,
            undefined,
            0.65,
          ) >= 0.999
        ) {
          let supported = true,
            y = from.y;
          const samples = Math.max(1, Math.ceil(Math.hypot(to.x - from.x, to.z - from.z) / 0.65));
          for (let i = 1; i <= samples; i++) {
            const t = i / samples,
              ground = physics.ground(
                { x: from.x + (to.x - from.x) * t, y, z: from.z + (to.z - from.z) * t },
                0.65,
                0.7,
              );
            if (!ground || Math.abs(ground.y - y) > 0.65) {
              supported = false;
              break;
            }
            y = ground.y;
            yield;
          }
          if (supported && Math.abs(y - to.y) < 0.7) found.push(n);
        }
        if (found.length === 3) break;
        yield;
      }
      return found;
    }
    const starts = yield* nearest(u.crew.p, true),
      ends = yield* nearest(point),
      exits = new Map<number, Vec[]>(),
      entries = new Map<number, Vec[]>();
    if (!starts.length) {
      const candidates = network
        .near(u.crew.p, 12)
        .filter((n) => !blocked.has(n.id) && Math.abs(n.y - u.crew.p.y) < 0.7 && connectedNode(n.id, true))
        .sort(
          (a, b) =>
            Math.hypot(a.x - u.crew.p.x, a.z - u.crew.p.z) - Math.hypot(b.x - u.crew.p.x, b.z - u.crew.p.z),
        )
        .slice(0, 3);
      for (const n of candidates) {
        const approach = yield* localFootRoute(physics, u.crew.p, n, 0.65);
        if (approach.length) {
          starts.push(n);
          entries.set(n.id, approach);
        }
      }
    }
    // A collapse can remove every authored floor node beside a wreck. Join
    // from a remaining public path through a bounded, physically supported
    // final approach instead of requiring a graph node within three metres.
    if (!ends.length) {
      const candidates = network
        .near(point, 12)
        .filter((n) => !blocked.has(n.id) && Math.abs(n.y - point.y) < 0.7 && connectedNode(n.id, false))
        .sort((a, b) => Math.hypot(a.x - point.x, a.z - point.z) - Math.hypot(b.x - point.x, b.z - point.z))
        .slice(0, 3);
      for (const n of candidates) {
        const approach = yield* localFootRoute(physics, n, point, 0.65);
        if (approach.length) {
          ends.push(n);
          exits.set(n.id, approach);
        }
      }
    }
    // A parked engine is an impassable body, unlike a soft crowd-cost node.
    for (const to of ends)
      for (const from of starts) {
        const path = yield* network.search(from.id, to.id, this.clock, blocked, true, true);
        if (path.length) {
          u.footBypass = entries.get(from.id);
          u.footExit = exits.get(to.id);
          return path;
        }
        yield;
      }
    u.planFailure = {
      starts: starts.length,
      ends: ends.length,
      reason: !starts.length
        ? 'No supported origin connection'
        : !ends.length
          ? 'No supported final approach'
          : 'No connected route between approaches',
    };
    return [];
  }
  /** Shared supported navigation for original military/auxiliary responders. */
  walkAssistingCrew(crew: Person, target: Vec, dt: number): boolean {
    if (this.population.stepYield(crew, dt)) return false;
    let path = this.assistingPaths.get(crew.id);
    if (!path) {
      path = { crew, incident: 'field-assistance', route: [], cursor: 0 };
      this.assistingPaths.set(crew.id, path);
      this.population.civil?.cancelTask(crew, 'Responding to field assistance assignment');
      this.crewRoute(path, target);
    } else if (
      !path.routePoint ||
      path.routePoint.distanceToSquared(new THREE.Vector3(target.x, target.y, target.z)) > 0.55 ** 2
    )
      this.crewRoute(path, target);
    crew.controlled = true;
    return this.walk(path, target, dt, 0.4);
  }
  assistanceRouteFailed(crew: Person): boolean {
    const path = this.assistingPaths.get(crew.id);
    return !!path?.planFailure && !path.planning;
  }
  releaseAssistingCrew(crew: Person): void {
    const path = this.assistingPaths.get(crew.id);
    if (path) this.cancelCrewRoute(path);
    this.assistingPaths.delete(crew.id);
  }
  private walk(u: CrewPath, target: Vec, dt: number, tolerance = 0.9): boolean {
    const crew = u.crew;
    if (crew.state === 'incapacitated' || crew.state === 'falling') return false;
    const distance = Math.hypot(target.x - crew.p.x, target.y - crew.p.y, target.z - crew.p.z);
    // Arrival wins over an obsolete route/probe. A crew already at its real
    // supported door must not wait for, or repeatedly relocate, that vehicle.
    if (distance < tolerance && this.physics.ground(crew.p, 0.35, 0.5)) {
      this.cancelCrewRoute(u);
      u.accessSince = undefined;
      u.footProgress = undefined;
      return true;
    }
    if (u.planning) {
      crew.state = 'wait';
      return false;
    }
    if (this.clock < (u.nextFootProbe ?? 0)) return false;
    const n = this.population.civil?.network;
    if (
      n &&
      !u.route.length &&
      !u.footBypass?.length &&
      !u.footExit?.length &&
      distance > 3 &&
      (!u.directGoal ||
        u.directGoal.distanceToSquared(new THREE.Vector3(target.x, target.y, target.z)) > 0.25)
    ) {
      // A failed graph route is an access failure, never a long straight walk
      // through buildings, a destroyed stairwell or a parked vehicle queue.
      u.accessSince ??= this.clock;
      crew.state = 'wait';
      if (this.clock > (u.routeRetry ?? 0)) this.crewRoute(u, target);
      const incident = u.incident && this.incidents.get(u.incident);
      if (incident) incident.reason = 'Crew waiting for a connected supported foot route';
      return false;
    }
    let goal = target;
    if (u.cursor >= u.route.length && u.footExit?.length) {
      u.footBypass = u.footExit;
      u.footExit = undefined;
    }
    if (u.footBypass?.length) {
      goal = u.footBypass[0];
      if (Math.hypot(goal.x - crew.p.x, goal.z - crew.p.z) < 0.1 && Math.abs(goal.y - crew.p.y) < 0.35) {
        u.footBypass.shift();
        return false;
      }
    } else if (n && u.cursor < u.route.length) {
      const node = n.nodes[u.route[u.cursor]],
        prior = u.cursor ? n.nodes[u.route[u.cursor - 1]] : undefined,
        edge = prior ? n.edge(prior.id, node.id) : undefined;
      if (edge && !n.open(edge, this.clock)) {
        this.crewRoute(u, target);
        return false;
      }
      if (edge?.kind === 'lift') {
        // Extraction waits for a staffed accessible route; no patient teleport through floors.
        const i = this.incidents.get(u.incident!);
        if (i) i.reason = 'Crew waiting for safe vertical extraction';
        u.accessSince ??= this.clock;
        return false;
      }
      goal = node;
      crew.layer = node.layer;
      if (Math.hypot(node.x - crew.p.x, node.z - crew.p.z) < 0.35 && Math.abs(node.y - crew.p.y) < 0.35) {
        u.cursor++;
        return false;
      }
    }
    if (distance < tolerance) {
      u.accessSince = undefined;
      return true;
    }
    crew.previous.copy(crew.p);
    crew.state = 'walk';
    if (!this.population.footClear(crew, goal)) {
      this.population.yieldFoot(crew, goal, dt);
      this.footProgress(u, goal);
      return false;
    }
    this.population.walkTo(
      crew,
      goal,
      dt,
      crew.speed * (u.incident && this.city.regional ? 5 : 1),
      !!u.footBypass?.length,
    );
    this.footProgress(u, goal);
    return false;
  }
  private footProgress(u: CrewPath, goal: Vec): void {
    const distance = Math.hypot(goal.x - u.crew.p.x, goal.z - u.crew.p.z),
      prior = u.footProgress;
    if (!prior || Math.hypot(prior.goal.x - goal.x, prior.goal.z - goal.z) > 0.05) {
      u.footProgress = { goal: { ...goal }, distance, at: this.clock };
    } else if (prior.distance - distance > 0.15) {
      u.footProgress = { goal: { ...goal }, distance, at: this.clock };
      u.accessSince = undefined;
    } else if (this.clock - prior.at > 1) {
      u.accessSince ??= prior.at;
      u.nextFootProbe = this.clock + 0.1;
      u.crew.state = 'wait';
      if (this.clock - prior.at > 1.5 && this.clock > (u.bypassRetry ?? 0) && !u.footBypass?.length) {
        u.bypassRetry = this.clock + 5;
        u.planning = this.bypassCrew(u, goal);
        this.crewPlans.add(u);
      } else if (this.clock - prior.at > 5 && this.clock > (u.routeRetry ?? 0) && u.routePoint)
        this.crewRoute(u, u.routePoint);
    }
  }
  private *bypassCrew(u: CrewPath, goal: Vec): Generator<void, number[]> {
    const remaining = u.route.slice(u.cursor);
    const bypass = yield* localFootRoute(this.physics, u.crew.p.clone(), goal, 0.65);
    u.crew.detour = undefined;
    if (bypass.length) {
      u.footBypass = bypass;
      u.footProgress = undefined;
    }
    return remaining;
  }
  private unloadCrew(u: Unit): boolean {
    if (!u.crew.hidden) return true;
    const car = u.car,
      incident = u.incident ? this.incidents.get(u.incident) : undefined,
      doors = [
        ...(u.state === 'transport' ? [new THREE.Vector3(0, 0, car.length / 2 + 0.75)] : []),
        ...[-1, 1].map((side) => new THREE.Vector3(side * (car.width / 2 + 0.65), 0, 0)),
      ]
        .map((local) => car.p.clone().add(local.applyQuaternion(car.q)))
        .flatMap((point) => {
          const support = this.physics.ground(point, 0.35, 0.5);
          if (
            !support ||
            !this.vehicleFree(point) ||
            this.physics.blocked(point.x, point.z, 0.28, support.y + 0.85)
          )
            return [];
          point.y = support.y;
          return [point];
        })
        .sort((a, b) =>
          incident ? a.distanceToSquared(incident.point) - b.distanceToSquared(incident.point) : 0,
        ),
      point = doors[0];
    // A sideways parked vehicle has two distinct exits. Always using its right
    // door stranded the tow operator behind its own body. Keep the original
    // person aboard if neither supported door can actually be used.
    if (!point) return false;
    u.crew.hidden = false;
    u.crew.state = 'service';
    u.crew.p.copy(point);
    u.crew.previous.copy(point);
    u.crew.velocity.set(0, 0, 0);
    u.unloadDoor = point.clone();
    return true;
  }
  private beginAdmission(u: Unit): boolean {
    const patient = u.patient && this.person(u.patient),
      hospital = u.destination,
      bed = hospital && u.bed !== undefined ? hospital.beds[u.bed] : undefined;
    if (
      !patient ||
      !hospital ||
      !bed ||
      !this.available(hospital) ||
      !this.fixedCell(bed.supportCell) ||
      u.car.actualSpeed >= 0.05 ||
      !this.unloadCrew(u)
    )
      return false;
    u.car.plan = [];
    u.car.state = 'deployed';
    u.car.actualSpeed = u.car.targetSpeed = 0;
    u.boardingFor = undefined;
    patient.hidden = false;
    patient.state = 'carried';
    u.state = 'admitting';
    u.crew.detour = undefined;
    u.admissionWait = undefined;
    this.crewRoute(u, bed);
    return true;
  }
  private vehicleFree(point: Vec): boolean {
    return !this.population.cars.some((car) => {
      if (Math.abs(car.p.y - point.y) > 2) return false;
      const x = point.x - car.p.x,
        z = point.z - car.p.z;
      // Exact oriented rectangle below; its enclosing circle cheaply rejects
      // the rest of the city before repeated trigonometry at each work point.
      if (x * x + z * z >= (car.width / 2 + 0.32) ** 2 + (car.length / 2 + 0.32) ** 2) return false;
      const cos = Math.cos(car.yaw),
        sin = Math.sin(car.yaw);
      return (
        Math.abs(x * cos - z * sin) < car.width / 2 + 0.32 &&
        Math.abs(x * sin + z * cos) < car.length / 2 + 0.32
      );
    });
  }
  private hoseStagingClear(u: Unit, point: Vec): boolean {
    const dx = point.x - u.car.p.x,
      dz = point.z - u.car.p.z,
      cos = Math.cos(u.car.yaw),
      sin = Math.sin(u.car.yaw),
      x = Math.max(0, Math.abs(dx * cos - dz * sin) - u.car.width / 2),
      z = Math.max(0, Math.abs(dx * sin + dz * cos) - u.car.length / 2);
    // Leave the doors and the immediate vehicle manoeuvring envelope usable.
    // Beyond it any supported position in hose reach is a valid work position.
    return x * x + z * z >= 2.8 * 2.8;
  }
  patientApproach(
    patient: Person,
    from: Vec,
    rejected: readonly THREE.Vector3[] = [],
  ): THREE.Vector3 | undefined {
    const candidates: THREE.Vector3[] = [];
    // A casualty on a low slab need not be stood on. The medic can reach from
    // adjacent supported ground, while a high or enclosed patient still needs
    // a real vertical route or air extraction.
    for (const radius of [0, 0.75, 1.15, 1.5])
      for (let k = 0; k < (radius ? 12 : 1); k++) {
        const angle = (k * Math.PI) / 6,
          point = patient.p
            .clone()
            .add(new THREE.Vector3(Math.cos(angle) * radius, 0, Math.sin(angle) * radius)),
          floor = this.physics.ground(point, 0.25, 1.15);
        if (!floor || Math.abs(floor.y - patient.p.y) > 1.05) continue;
        point.y = floor.y;
        if (
          rejected.some((old) => old.distanceToSquared(point) < 0.3 * 0.3) ||
          !this.vehicleFree(point) ||
          this.physics.blocked(point.x, point.z, 0.26, point.y + 0.85) ||
          !this.treatmentOpen(point, patient)
        )
          continue;
        candidates.push(point);
      }
    candidates.sort(
      (a, b) =>
        Math.hypot(a.x - from.x, a.y - from.y, a.z - from.z) -
        Math.hypot(b.x - from.x, b.y - from.y, b.z - from.z),
    );
    return candidates[0];
  }
  canTreat(crew: Person, patient: Person): boolean {
    return this.treatmentOpen(crew.p, patient) && !!this.physics.ground(crew.p, 0.12, 0.3);
  }
  private treatmentOpen(point: Vec, patient: Person): boolean {
    if (patient.p.distanceTo(point) >= 1.65 || Math.abs(point.y - patient.p.y) > 1.05) return false;
    const from = new THREE.Vector3(point.x, point.y + 1, point.z),
      delta = patient.p
        .clone()
        .add(new THREE.Vector3(0, 0.45, 0))
        .sub(from),
      span = delta.length();
    return !this.physics.ray(from, delta.normalize(), Math.max(0, span - 0.08), undefined, {
      physical: true,
      ignoreEntities: true,
    });
  }
  private retryPatientApproach(u: Unit, patient: Person): void {
    if (u.planning || this.clock < (u.patientApproachRetry ?? 0)) return;
    const moved = !u.patientRoutePosition || u.patientRoutePosition.distanceTo(patient.p) > 1.7;
    const stalled = u.accessSince !== undefined && this.clock - u.accessSince > 6;
    if (!moved && !u.planFailure && !stalled) return;
    u.patientApproachRetry = this.clock + 6;
    if (moved) u.rejectedPatientApproaches = [];
    else if (u.routePoint) {
      (u.rejectedPatientApproaches ??= []).push(u.routePoint.clone());
      // A small retry history avoids oscillating between the same two blocked
      // sides, while allowing changed rubble/traffic to reopen an older one.
      if (u.rejectedPatientApproaches.length > 8) u.rejectedPatientApproaches.shift();
    }
    u.patientRoutePosition = patient.p.clone();
    const approach = this.patientApproach(patient, u.crew.p, u.rejectedPatientApproaches);
    if (!approach) return;
    this.crewRoute(u, approach);
    u.footProgress = undefined;
    this.record(u, 'Trying another supported treatment approach to the same casualty', patient.id);
  }
  private loadMedical(u: Unit, patient: Person): boolean {
    if (this.clock < (u.hospitalRetry ?? 0)) return false;
    u.hospitalRetry = this.clock + 2;
    const hospitals = (this.city.facilities ?? [])
      .filter((f) => f.kind === 'hospital' && this.available(f))
      .sort(
        (a, b) =>
          Math.hypot(a.entry.x - u.car.p.x, a.entry.z - u.car.p.z) -
          Math.hypot(b.entry.x - u.car.p.x, b.entry.z - u.car.p.z),
      );
    for (const hospital of hospitals) {
      const bed = hospital.beds.findIndex((_, i) =>
        this.reservations.acquire(`bed:${hospital.id}:${i}`, u.id, this.clock, 1, 300),
      );
      if (bed < 0) continue;
      for (const [index, bay] of hospital.bays.entries()) {
        if (
          this.population.cars.some(
            (c) => c !== u.car && Math.hypot(c.p.x - bay.x, c.p.y - bay.y, c.p.z - bay.z) < 3,
          )
        )
          continue;
        const resource = `hospital-bay:${hospital.id}:${index}`;
        if (!this.reservations.acquire(resource, u.id, this.clock, 1, 300)) continue;
        const alreadyAtBay =
          u.car.p.distanceTo(new THREE.Vector3(bay.x, bay.y, bay.z)) < 0.8 && u.car.actualSpeed < 0.05;
        if (!alreadyAtBay && !this.routeVehicle(u, bay, hospital)) {
          this.reservations.release(u.id, resource);
          continue;
        }
        if (alreadyAtBay) {
          u.car.plan = [];
          u.car.state = 'deployed';
          u.car.actualSpeed = u.car.targetSpeed = 0;
          u.boardingFor = undefined;
          this.record(
            u,
            'already at a reserved hospital bay; admitting without an unnecessary drive',
            patient.id,
          );
        }
        // Commit boarding only with a real reserved bed, usable bay and route.
        // A busy nearest hospital cannot hide a patient in a carrying state.
        u.destination = hospital;
        u.bed = bed;
        u.hospitalBay = resource;
        patient.hidden = true;
        patient.state = 'aboard';
        u.crew.hidden = true;
        u.state = 'transport';
        this.record(u, 'loaded; transporting to hospital', patient.id);
        return true;
      }
      this.reservations.release(u.id, `bed:${hospital.id}:${bed}`);
    }
    const incident = u.incident && this.incidents.get(u.incident);
    if (incident)
      incident.reason = 'Patient stabilised beside ambulance; waiting for a reachable hospital bed and bay';
    return false;
  }
  private workPoint(u: Unit, member: number): THREE.Vector3 | undefined {
    const i = u.incident ? this.incidents.get(u.incident) : undefined,
      network = this.population.civil?.network;
    if (!i || !network) return;
    const subject = u.patient
      ? this.person(u.patient)?.p
      : u.wreck
        ? this.population.cars.find((c) => c.id === u.wreck)?.p
        : undefined;
    if (subject && (u.role === 'medical' || u.role === 'tow')) {
      for (const sign of [1, -1]) {
        const angle = member * 1.35 + this.units.indexOf(u) * 0.7,
          p = subject
            .clone()
            .add(new THREE.Vector3(Math.cos(angle) * sign * 1.1, 0, Math.sin(angle) * sign * 1.1)),
          support = this.physics.ground(p, 0.35, 0.5);
        if (support && this.vehicleFree(p) && !this.physics.blocked(p.x, p.z, 0.26, support.y + 0.85)) {
          p.y = support.y;
          return p;
        }
      }
    }
    const direction = u.car.p.clone().sub(i.point).setY(0).normalize(),
      tangent = new THREE.Vector3(-direction.z, 0, direction.x),
      wanted = i.point
        .clone()
        .addScaledVector(direction, u.role === 'fire' ? 20 : 30)
        .addScaledVector(tangent, (member - 2) * 2.2);
    const candidates = network
      .near(wanted, 28, 'street')
      .sort(
        (a, b) => Math.hypot(a.x - wanted.x, a.z - wanted.z) - Math.hypot(b.x - wanted.x, b.z - wanted.z),
      );
    // Find the nearest valid destination. Filtering every point first repeated
    // vehicle/physics queries across the entire neighbourhood for every medic.
    const n = candidates.find(
      (n) =>
        Math.abs(n.y - u.car.p.y) < 1 &&
        (u.role !== 'fire' || this.hoseStagingClear(u, n)) &&
        (u.role !== 'fire' || Math.hypot(n.x - i.point.x, n.z - i.point.z) < 30) &&
        this.vehicleFree(n) &&
        !this.physics.blocked(n.x, n.z, 0.26, n.y + 0.85) &&
        !(member && u.routePoint && Math.hypot(n.x - u.routePoint.x, n.z - u.routePoint.z) < 0.65) &&
        !this.units.some((other) =>
          other.additional.some((m) => m.goal && Math.hypot(m.goal.x - n.x, m.goal.z - n.z) < 0.65),
        ),
    );
    return n ? new THREE.Vector3(n.x, n.y, n.z) : undefined;
  }
  private updateTeam(u: Unit, dt: number): void {
    if (!u.additional.length) return;
    const car = u.car,
      incident = u.incident ? this.incidents.get(u.incident) : undefined;
    const needBoard =
      u.crew.hidden ||
      !incident ||
      incident.state === 'complete' ||
      ['muster', 'crew-return', 'returning', 'turnaround', 'transport', 'admitting', 'unavailable'].includes(
        u.state,
      );
    for (const [index, m] of u.additional.entries()) {
      const p = m.crew;
      const memberBoard = needBoard || (index === 0 && u.driverRetained);
      if (m.incident !== u.incident) {
        this.cancelCrewRoute(m);
        m.goal = m.goalSubject = undefined;
        m.workRetry = undefined;
        m.incident = u.incident;
      }
      if (car.state === 'wreck') {
        if (p.hidden) {
          p.hidden = false;
          p.p.copy(car.p);
          p.previous.copy(p.p);
          p.velocity.copy(car.body.linvel());
          p.state = 'incapacitated';
          p.controlled = false;
        }
        continue;
      }
      if (['incapacitated', 'falling', 'flee'].includes(p.state)) continue;
      if (this.population.stepYield(p, dt)) continue;
      if (p.hidden) {
        p.previous.copy(p.p);
        // Original seated crew share the vehicle pose until a real door opens.
        p.p
          .copy(car.p)
          .add(
            new THREE.Vector3(index % 2 ? 0.5 : -0.5, 0, 0.25 + Math.floor(index / 2) * 0.8).applyQuaternion(
              car.q,
            ),
          );
        m.state = 'aboard';
        if (
          memberBoard ||
          car.state !== 'deployed' ||
          this.clock < u.egressAt ||
          (index === 0 && u.driverRetained)
        )
          continue;
        let exit: THREE.Vector3 | undefined;
        for (const [sign, along] of [1, -1].flatMap((sign) =>
          [-0.65 + index * 0.45, -1.2, 1.2].map((along) => [sign, along]),
        )) {
          const next = car.p
              .clone()
              .add(new THREE.Vector3(sign * (car.width / 2 + 0.7), 0, along).applyQuaternion(car.q)),
            support = this.physics.ground(next, 0.35, 0.5);
          if (
            !support ||
            !this.vehicleFree(next) ||
            this.physics.blocked(next.x, next.z, 0.27, support.y + 0.85)
          )
            continue;
          next.y = support.y;
          if (
            this.population.people.some(
              (other) =>
                other !== p &&
                !other.hidden &&
                Math.abs(other.p.y - next.y) < 0.6 &&
                Math.hypot(other.p.x - next.x, other.p.z - next.z) < 0.62,
            )
          )
            continue;
          exit = next;
          break;
        }
        if (!exit) continue;
        p.hidden = false;
        p.p.copy(exit);
        p.previous.copy(exit);
        p.state = 'service';
        u.egressAt = this.clock + 0.3;
        m.state = 'deploying';
        m.goal = this.workPoint(u, index + 1);
        if (m.goal) this.crewRoute(m, m.goal);
        this.record(u, 'crew member disembarked', p.id);
      }
      if (memberBoard) {
        this.assistance.release(p);
        if (u.rendezvousMoving) {
          p.state = 'wait';
          continue;
        }
        const door = this.crewDoor(u);
        if (m.state !== 'returning') {
          m.state = 'returning';
          m.goal = door;
          this.crewRoute(m, door);
        }
        if (this.walk(m, door, dt, 0.45)) {
          p.hidden = true;
          p.state = 'service';
          m.state = 'aboard';
          m.goal = undefined;
          this.record(u, 'crew member boarded', p.id);
        }
        continue;
      }
      if (
        incident &&
        (u.role === 'police' || u.role === 'tow') &&
        this.assistance.step(p, incident.point, dt)
      ) {
        this.cancelCrewRoute(m);
        m.state = 'working';
        continue;
      }
      const patientPoint = u.patient && this.person(u.patient)?.p;
      const subjectMoved =
        patientPoint && (!m.goalSubject || m.goalSubject.distanceToSquared(patientPoint) > 2.25);
      const seekCloser =
        patientPoint && m.state === 'working' && m.goal && m.goal.distanceToSquared(patientPoint) > 9;
      if (!m.goal || (!m.planning && (subjectMoved || seekCloser) && this.clock >= (m.workRetry ?? 0))) {
        if (!m.goal && this.clock < (m.workRetry ?? 0)) continue;
        m.workRetry = this.clock + 2;
        m.goalSubject = patientPoint ? patientPoint.clone() : undefined;
        const goal = this.workPoint(u, index + 1);
        if (goal && (!m.goal || goal.distanceToSquared(m.goal) > 0.04)) {
          m.goal = goal;
          this.crewRoute(m, goal);
        }
      }
      const fires = this.liveFires.filter(
        (f) =>
          incident?.fires.has(f.id) &&
          p.p.distanceTo(new THREE.Vector3(f.point.x, f.point.y, f.point.z)) < 32,
      );
      // A supported responder already within hose reach can work immediately;
      // a congested preferred formation slot must not block all suppression.
      if (
        !(u.role === 'fire' && fires.length && this.hoseStagingClear(u, p.p)) &&
        (!m.goal || !this.walk(m, m.goal, dt, 0.55))
      )
        continue;
      m.state = 'working';
      if (u.role === 'fire' && fires.length && u.water > 0) {
        p.state = 'suppressing';
        for (const f of fires) {
          const amount = (dt * 0.07) / fires.length;
          this.fire.cool(f.id, amount);
          this.waterApplied += amount;
          m.work += amount;
        }
        u.water = Math.max(0, u.water - dt * 0.003);
      } else if (u.role === 'medical') {
        p.state = 'assessing';
        const patient = this.person(u.patient);
        if (patient && p.p.distanceTo(patient.p) < 2.5 && u.state === 'stabilising') {
          u.timer -= dt * 0.12;
          m.work += dt;
        }
      } else if (u.role === 'police') {
        p.state = 'directing';
        m.work += dt;
      } else {
        p.state = u.role === 'tow' ? 'inspecting equipment' : 'service';
        if (u.state === 'hooking') {
          u.timer -= dt * 0.12;
          m.work += dt;
        }
      }
    }
    const outside = u.additional.some(
      (m) => !m.crew.hidden && !['incapacitated', 'falling', 'flee'].includes(m.crew.state),
    );
    if (u.crew.hidden && car.plan.length && outside && !['wreck', 'deployed'].includes(car.state)) {
      u.boardingFor ??= car.state;
      car.state = 'station';
      car.actualSpeed = car.targetSpeed = 0;
    } else if (!outside && u.boardingFor && car.state !== 'wreck') {
      car.state = u.boardingFor;
      u.boardingFor = undefined;
    }
  }
  private returnUnit(u: Unit): void {
    u.pickupWaiting = false;
    this.cancelCrewRoute(u);
    u.accessSince = undefined;
    u.footProgress = undefined;
    this.reservations.release(u.id);
    u.hospitalBay = undefined;
    u.hospitalRetry = undefined;
    u.admissionWait = undefined;
    u.patient = undefined;
    u.airHandoffRequested = undefined;
    u.patientRoutePosition = undefined;
    u.patientApproachRetry = undefined;
    u.rejectedPatientApproaches = undefined;
    u.wreck = undefined;
    u.route = u.crew.hidden ? [] : u.route.reverse();
    u.cursor = 0;
    u.crew.detour = undefined;
    u.destination = undefined;
    u.footFallback = false;
    if (!u.crew.hidden) {
      u.state = 'crew-return';
      if (this.city.regional) this.crewRoute(u, this.crewDoor(u));
      return;
    }
    this.driveHome(u);
  }
  private relocateRendezvous(u: Unit, pickup?: TrafficVehicle): boolean {
    const driver = u.driverRetained ? u.additional[0]?.crew : pickup && u.crew.hidden ? u.crew : undefined;
    if (
      !driver ||
      !driver.hidden ||
      ['incapacitated', 'falling', 'flee'].includes(driver.state) ||
      this.clock < (u.rendezvousRetry ?? 0) ||
      u.car.state === 'wreck'
    )
      return false;
    u.rendezvousRetry = this.clock + 12;
    const network = this.population.network,
      goal = pickup ? this.wreckWorkPoint(pickup, u.car.p) : u.crew.p;
    for (const approach of network.approaches(goal, true, u.car.length, 8)) {
      const edge = approach.edge,
        a = network.nodes.get(edge.from)!,
        b = network.nodes.get(edge.to)!;
      const length = Math.hypot(b.x - a.x, b.z - a.z),
        dx = (b.x - a.x) / length,
        dz = (b.z - a.z) / length;
      for (const sign of [1, -1]) {
        const point = new THREE.Vector3(approach.point.x, approach.point.y, approach.point.z).add(
          new THREE.Vector3(-dz, 0, dx).multiplyScalar(
            sign * Math.max(0.5, edge.width / 2 - u.car.width / 2 - 0.25),
          ),
        );
        const support = this.physics.ground(point, 0.35, 0.5);
        if (
          !support ||
          Math.abs(support.y - goal.y) > 0.55 ||
          point.distanceTo(goal) > 40 ||
          (pickup && point.distanceTo(pickup.p) > (u.car.length + pickup.length) / 2 + 2) ||
          point.distanceTo(u.car.p) < (pickup ? 1 : 8)
        )
          continue;
        point.y = support.y;
        const yaw = Math.atan2(-dx, -dz),
          q = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), yaw);
        const half = u.car.collider.halfExtents()!,
          local = u.car.collider.translationWrtParent()!;
        half.z = u.car.length / 2;
        if (
          !this.physics.occupiedBoxClear(
            { x: point.x, y: point.y + local.y, z: point.z },
            half,
            q,
            { x: 0, y: 0, z: 0 },
            u.car.body,
          )
        )
          continue;
        const door = point.clone().add(new THREE.Vector3(u.car.width / 2 + 0.7, 0, 0).applyQuaternion(q));
        const floor = this.physics.ground(door, 0.35, 0.5);
        if (
          !floor ||
          Math.abs(floor.y - goal.y) > 0.55 ||
          !this.vehicleFree(door) ||
          this.physics.blocked(door.x, door.z, 0.26, floor.y + 0.85)
        )
          continue;
        if (!this.routeVehicle(u, point)) continue;
        u.car.parkingYaw = yaw;
        u.rendezvousMoving = true;
        u.rendezvousStarted = this.clock;
        u.unloadDoor = undefined;
        this.cancelCrewRoute(u);
        for (const m of u.additional) {
          this.cancelCrewRoute(m);
          m.goal = undefined;
        }
        this.record(u, 'retained driver relocating original vehicle to a reachable crew rendezvous');
        return true;
      }
    }
    return false;
  }
  private driveHome(u: Unit): void {
    u.crew.hidden = true;
    if (!this.available(u.facility)) {
      u.state = 'unavailable';
      u.car.state = 'parked';
      this.record(u, 'base unavailable');
      return;
    }
    if (u.car.p.distanceTo(u.car.home) < 1.3) {
      u.state = 'turnaround';
      u.timer = 8;
      u.incident = undefined;
      u.car.incident = undefined;
      return;
    }
    if (this.routeVehicle(u, u.car.home, u.facility)) {
      u.state = 'returning';
      this.record(u, 'returning');
    } else {
      u.state = 'unavailable';
      u.recoverable = 'route';
      u.recoveryRetry = this.clock + 10;
      this.record(u, 'return route unavailable');
    }
  }
  update(dt: number): void {
    if (this.epoch !== this.physics.life.epoch) return;
    this.clock += dt;
    this.assistance.update(dt);
    this.age += dt;
    this.liveFires = this.fire.incidents();
    const planningStart = performance.now();
    while (this.crewPlans.size && performance.now() - planningStart < (this.framePlanningMs ?? 0.8)) {
      const path = this.crewPlans.values().next().value!;
      this.crewPlans.delete(path);
      const result = path.planning!.next();
      if (!result.done) this.crewPlans.add(path);
      else {
        path.planning = undefined;
        path.route = result.value;
        path.cursor = 0;
        path.routeRetry = this.clock + (result.value.length ? 3 : 2);
        // Planning is not physical progress. Preserve stall history until
        // walk() proves movement; a failed bypass may return the same route.
      }
    }
    if (this.framePlanningMs !== undefined)
      this.framePlanningMs = Math.max(0, this.framePlanningMs - (performance.now() - planningStart));
    if (this.age >= 0.5) {
      this.age = 0;
      this.scan();
    }
    for (const u of this.units) {
      const car = u.car,
        incident = u.incident ? this.incidents.get(u.incident) : undefined;
      if (
        u.state !== 'unavailable' &&
        car.state !== 'wreck' &&
        ['incapacitated', 'falling', 'flee'].includes(u.crew.state)
      ) {
        // An injured driver cannot keep driving, cooling or carrying. The
        // surviving original team returns to the stopped vehicle; another
        // available unit can claim the released patient and assignment.
        this.cancelCrewRoute(u);
        this.reservations.release(u.id);
        car.plan = [];
        car.actualSpeed = car.targetSpeed = 0;
        car.state = 'parked';
        car.incident = undefined;
        u.state = 'unavailable';
        u.recoverable = u.crew.fatal ? undefined : 'crew';
        u.recoveryRetry = this.clock + 1;
        u.crew.controlled = false;
        if (u.patient) {
          const patient = this.person(u.patient);
          if (patient) {
            if (patient.hidden) {
              patient.p.copy(car.p);
              patient.previous.copy(patient.p);
            }
            patient.hidden = patient.controlled = false;
            patient.state = 'incapacitated';
            this.relocateCasualty(patient);
          }
          u.patient = undefined;
        }
        this.record(u, 'lead responder unsafe or injured; assignment released pending recovery');
      }
      if (car.state === 'wreck') {
        if (u.state !== 'unavailable') {
          u.state = 'unavailable';
          this.reservations.release(u.id);
          const aboard = u.crew.hidden;
          u.crew.hidden = false;
          u.crew.controlled = false;
          if (aboard) {
            u.crew.p.copy(car.p);
            u.crew.state = 'incapacitated';
          } else if (!['incapacitated', 'falling'].includes(u.crew.state))
            this.population.evacuate(u.crew, car.p);
          if (u.patient) {
            const p = this.person(u.patient)!;
            const patientAboard = p.hidden;
            p.hidden = false;
            p.controlled = false;
            if (patientAboard) {
              p.p.copy(car.p);
              p.previous.copy(p.p);
              p.velocity.copy(car.body.linvel());
            }
            p.state = 'incapacitated';
            this.record(u, 'patient awaiting replacement transport', p.id);
          }
        }
        continue;
      }
      if (u.crew.hidden) {
        u.crew.previous.copy(u.crew.p);
        u.crew.p.copy(car.p);
      }
      if (this.population.stepYield(u.crew, dt)) continue;
      if (u.state === 'unavailable' && u.recoverable && this.clock >= (u.recoveryRetry ?? 0)) {
        u.recoveryRetry = this.clock + 5;
        if (
          !u.crew.fatal &&
          !['incapacitated', 'falling', 'flee', 'carried', 'recovering'].includes(u.crew.state) &&
          this.available(u.facility)
        ) {
          const reason = u.recoverable;
          u.recoverable = undefined;
          u.crew.controlled = true;
          if (reason === 'route') this.driveHome(u);
          else this.returnUnit(u);
          this.record(
            u,
            reason === 'crew'
              ? 'original responder recovered; regrouping the same team and vehicle'
              : 'retrying original return route after obstruction',
          );
        }
      }
      if (u.state === 'ready') {
        if (!this.available(u.facility)) {
          u.state = 'unavailable';
          if (!u.crew.hidden) {
            u.crew.controlled = false;
            const support = this.physics.ground(u.crew.p, 0.28, 0.4);
            if (!support) {
              u.crew.state = 'falling';
              u.crew.fallStart = u.crew.p.y;
              u.crew.velocity.set(0, 0, 0);
            } else this.population.evacuate(u.crew, u.car.p);
          }
        } else this.stationDuty(u, dt);
        continue;
      }
      if (u.state === 'muster') {
        if (!incident || incident.state === 'complete' || (u.role === 'fire' && !incident.fires.size)) {
          u.incident = undefined;
          car.incident = undefined;
          car.plan = [];
          car.state = 'station';
          u.state = 'ready';
        } else if (this.walk(u, this.crewDoor(u), dt, 0.3)) {
          u.crew.hidden = true;
          u.crew.state = 'service';
          u.state = 'outbound';
          car.state = 'enroute';
          this.record(u, 'original crew boarded for dispatch');
        }
        continue;
      }
      if (u.state === 'unavailable') continue;
      if (
        u.state === 'carrying' &&
        u.patient &&
        u.accessSince !== undefined &&
        this.clock - u.accessSince > 30 &&
        this.population.airSupport?.units.some(
          (air) => air.pad.role === 'medical' && !['wreck', 'unavailable'].includes(air.state),
        )
      ) {
        const patient = this.person(u.patient);
        const floor = patient && this.physics.ground(patient.p, 0.2, 1.4);
        if (patient && !patient.fatal && floor) {
          // The original patient is lowered from the carrying pose onto its
          // current supported surface. A blocked vehicle rendezvous must not
          // keep the only patient reservation forever while air rescue is idle.
          patient.p.y = floor.y;
          patient.previous.copy(patient.p);
          patient.hidden = patient.controlled = false;
          patient.state = 'incapacitated';
          patient.velocity.set(0, 0, 0);
          this.airAccess.set(patient.id, this.clock + 240);
          this.record(
            u,
            'Carrying route and rendezvous blocked; original patient lowered for air extraction',
            patient.id,
          );
          this.returnUnit(u);
          continue;
        }
      }
      if (u.rendezvousMoving) {
        const driver = u.additional[0]?.crew ?? (u.pickupWaiting ? u.crew : undefined);
        if (!driver?.hidden || ['incapacitated', 'falling', 'flee'].includes(driver.state)) {
          car.plan = [];
          car.actualSpeed = car.targetSpeed = 0;
          car.state = 'parked';
          u.rendezvousMoving = false;
          this.record(u, 'rendezvous halted because the retained driver is unavailable');
          continue;
        }
        if (car.state !== 'deployed') {
          if (this.clock < (u.rendezvousProbe ?? 0)) {
            u.crew.state = 'wait';
            continue;
          }
          u.rendezvousProbe = this.clock + 0.5;
          // Congestion can stop the replacement pickup beside its own crew.
          // Use that real supported door rather than waiting forever for an
          // arbitrary later road coordinate while the crew stands within reach.
          const door = this.crewDoor(u),
            network = this.population.civil?.network,
            pickup = u.pickupWaiting && this.population.cars.find((c) => c.id === u.wreck),
            pickupClose = !pickup || car.p.distanceTo(pickup.p) <= (car.length + pickup.length) / 2 + 2.5;
          if (
            pickupClose &&
            car.actualSpeed < 0.05 &&
            this.physics.ground(door, 0.35, 0.5) &&
            ((door.distanceTo(u.crew.p) < 6 && network?.lineOpen(u.crew.p, door)) ||
              (car.blockedFor > 2 &&
                u.rendezvousStarted !== undefined &&
                this.clock - u.rendezvousStarted > 20))
          ) {
            car.plan = [];
            car.targetSpeed = 0;
            car.state = 'deployed';
            u.unloadDoor = door.clone();
            this.record(u, 'congested rendezvous accepting the current reachable crew door');
          } else {
            u.crew.state = 'wait';
            continue;
          }
        }
        u.rendezvousMoving = false;
        u.rendezvousStarted = undefined;
        u.accessSince = undefined;
        u.footProgress = undefined;
        if (u.pickupWaiting && u.role === 'tow' && u.wreck) {
          const wreck = this.population.cars.find((c) => c.id === u.wreck);
          if (!wreck || !this.unloadCrew(u)) continue;
          u.state = 'approaching';
          this.crewRoute(u, this.wreckWorkPoint(wreck, u.crew.p));
          this.record(u, 'Original recovery truck reached the wreck; operator approaching the hook');
          continue;
        }
        u.unloadDoor ??= this.crewDoor(u);
        this.crewRoute(u, this.crewDoor(u));
        for (const m of u.additional)
          if (!m.crew.hidden) {
            m.state = 'returning';
            m.accessSince = undefined;
            m.footProgress = undefined;
            this.crewRoute(m, this.crewDoor(u));
          }
        this.record(u, 'original vehicle arrived at replacement rendezvous');
      }
      if (
        u.state === 'outbound' &&
        (incident?.state === 'complete' || (u.role === 'fire' && incident && !incident.fires.size))
      ) {
        this.returnUnit(u);
        continue;
      }
      if (u.state === 'crew-return') {
        if (
          u.crew.p.distanceTo(this.crewDoor(u)) > 3 &&
          u.accessSince !== undefined &&
          this.clock - u.accessSince > 8 &&
          this.relocateRendezvous(u)
        )
          continue;
        if (u.handoffExit) {
          // Clear the shared carrying pose before following the return route.
          // Both the helper and patient keep their ordinary body clearance.
          const path: CrewPath = { crew: u.crew, route: [], cursor: 0 };
          if (!this.walk(path, u.handoffExit, dt, 0.2)) continue;
          u.handoffExit = undefined;
        }
        const door =
          u.unloadDoor ??
          car.p.clone().add(new THREE.Vector3(car.width / 2 + 0.7, 0, 0).applyQuaternion(car.q));
        if (this.walk(u, door, dt)) this.driveHome(u);
        continue;
      }
      if (u.state === 'returning' && car.state === 'deployed') {
        u.state = 'turnaround';
        u.timer = 8;
        u.incident = undefined;
        car.incident = undefined;
        this.record(u, 'returned to base');
      }
      if (u.state === 'turnaround') {
        u.timer -= dt;
        if (u.timer <= 0) {
          if (!u.duty) u.water = 1;
          u.state = this.available(u.facility) ? 'ready' : 'unavailable';
          car.state = 'station';
          u.driverRetained = false;
        }
        continue;
      }
      if (u.state === 'outbound' && car.state === 'deployed') {
        if (!this.unloadCrew(u)) continue;
        u.state = u.role === 'fire' ? 'suppression' : u.role === 'police' ? 'control' : 'approaching';
        this.record(u, 'arrived');
        if (u.role === 'fire' && this.city.regional) {
          const work = this.workPoint(u, 0);
          if (work) this.crewRoute(u, work);
        }
        if (u.role === 'medical' && incident) {
          const p = this.population.people
            .filter(
              (p) =>
                incident.people.has(p.id) &&
                p.state === 'incapacitated' &&
                (this.airAccess.get(p.id) ?? 0) <= this.clock,
            )
            .find((p) => this.reservations.acquire(`patient:${p.id}`, u.id, this.clock, 1, 120));
          if (p) {
            u.patient = p.id;
            u.patientRoutePosition = p.p.clone();
            u.patientApproachRetry = this.clock + 6;
            u.rejectedPatientApproaches = [];
            this.crewRoute(u, this.patientApproach(p, u.crew.p) ?? p.p);
          } else this.returnUnit(u);
        }
        if (u.role === 'tow' && incident) {
          const w = this.population.cars
            .filter((c) => incident.wrecks.has(c.id) && c.state === 'wreck')
            .find((c) => this.reservations.acquire(`wreck:${c.id}`, u.id, this.clock, 1, 180));
          if (w) {
            u.wreck = w.id;
            this.crewRoute(u, this.wreckWorkPoint(w, u.crew.p));
          } else this.returnUnit(u);
        }
      }
      if (u.state === 'suppression') {
        if (!incident?.fires.size || u.water <= 0) {
          this.returnUnit(u);
          continue;
        }
        const active = this.liveFires.filter((f) => incident.fires.has(f.id));
        if (
          !this.hoseStagingClear(u, u.crew.p) ||
          !active.some((f) => u.crew.p.distanceTo(new THREE.Vector3(f.point.x, f.point.y, f.point.z)) < 32)
        ) {
          if (
            (!u.routePoint || (!u.planning && !u.directGoal && u.cursor >= u.route.length)) &&
            this.clock > (u.routeRetry ?? 0)
          ) {
            u.routeRetry = this.clock + 3;
            const work = this.workPoint(u, 0);
            if (work) this.crewRoute(u, work);
          }
          if (u.routePoint) this.walk(u, u.routePoint, dt, 0.6);
          continue;
        }
        for (const f of active)
          if (u.crew.p.distanceTo(new THREE.Vector3(f.point.x, f.point.y, f.point.z)) < 32) {
            const amount = (dt * 0.22) / active.length;
            this.fire.cool(f.id, amount);
            this.waterApplied += amount;
          }
        u.water = Math.max(0, u.water - dt * 0.004);
        u.crew.state = 'suppressing';
      }
      if (u.state === 'control') {
        u.crew.state = 'directing';
        if (!incident || incident.state === 'complete') this.returnUnit(u);
      }
      if (u.role === 'medical' && u.patient) {
        const p = this.person(u.patient);
        if (!p) continue;
        if (u.airHandoffRequested) {
          const accepted = this.population.airSupport?.units.find(
            (air) =>
              air.patient === p && p.controlled && ['hoisting', 'returning', 'handoff'].includes(air.state),
          );
          if (accepted) {
            this.record(
              u,
              `Air rescue ${accepted.id} took custody of original patient; ground crew returning`,
              p.id,
            );
            this.returnUnit(u);
            continue;
          }
          // Requesting a helicopter is not a completed handoff. Keep the
          // original ground team assigned and retry real access until an air
          // unit has physically taken custody or ground care can start.
          if (
            this.canTreat(u.crew, p) &&
            this.reservations.acquire(`patient:${p.id}`, u.id, this.clock, 1, 120)
          ) {
            u.airHandoffRequested = false;
            this.airAccess.delete(p.id);
          } else {
            this.airAccess.set(p.id, this.clock + 120);
            if (incident)
              incident.reason =
                'Ground team retaining casualty assignment; awaiting safe foot access or confirmed air rescue';
          }
        }
        if (p.fatal) {
          p.hidden = p.controlled = false;
          p.state = 'incapacitated';
          this.record(u, 'direct projectile casualty; release treatment without resurrection', p.id);
          this.returnUnit(u);
          continue;
        }
        if (
          u.state === 'approaching' &&
          !u.planning &&
          !u.airHandoffRequested &&
          !this.canTreat(u.crew, p) &&
          u.accessSince !== undefined &&
          this.clock - u.accessSince > 8 &&
          this.population.airSupport?.units.some(
            (unit) => unit.pad.role === 'medical' && !['wreck', 'unavailable'].includes(unit.state),
          )
        ) {
          this.reservations.release(u.id, `patient:${p.id}`);
          this.airAccess.set(p.id, this.clock + 120);
          u.airHandoffRequested = true;
          this.record(
            u,
            'Foot access delayed; requesting air rescue while retaining the original ground team',
            p.id,
          );
        }
        if (!u.airHandoffRequested) this.reservations.acquire(`patient:${p.id}`, u.id, this.clock, 1, 120);
        if (u.hospitalBay) this.reservations.acquire(u.hospitalBay, u.id, this.clock, 1, 300);
        if (u.state === 'approaching' && !this.canTreat(u.crew, p)) this.retryPatientApproach(u, p);
        if (
          u.state === 'approaching' &&
          (this.canTreat(u.crew, p) ||
            (this.walk(u, u.routePoint ?? p.p, dt, 0.3) && this.canTreat(u.crew, p))) &&
          this.reservations.acquire(`patient:${p.id}`, u.id, this.clock, 1, 120)
        ) {
          u.airHandoffRequested = false;
          this.airAccess.delete(p.id);
          this.cancelCrewRoute(u);
          u.state = 'stabilising';
          u.timer = this.fieldStabilised.delete(p.id) ? 1 : 5;
          u.crew.state = 'assessing';
          p.controlled = true;
          p.velocity.set(0, 0, 0);
          this.record(u, 'stabilising', p.id);
        }
        if (u.state === 'stabilising') {
          u.timer -= dt;
          if (u.timer <= 0) {
            u.state = 'carrying';
            u.route.reverse();
            u.cursor = 0;
            u.crew.detour = undefined;
            this.crewRoute(u, this.crewDoor(u));
            p.state = 'carried';
            this.record(u, 'carrying to ambulance', p.id);
          }
        }
        if (u.state === 'carrying') {
          if (u.accessSince !== undefined && this.clock - u.accessSince > 8 && this.relocateRendezvous(u))
            continue;
          const door = this.crewDoor(u);
          const arrived = this.walk(u, door, dt);
          p.previous.copy(p.p);
          p.p.copy(u.crew.p).add(new THREE.Vector3(0.25, 0.45, 0));
          if (arrived) this.loadMedical(u, p);
        }
        if (u.state === 'transport') {
          if (car.actualSpeed > 0.2) u.admissionWait = undefined;
          p.previous.copy(p.p);
          p.p.copy(car.p).add(new THREE.Vector3(0, 1, 0.4));
          if (car.state === 'deployed' && u.destination) {
            if (!this.beginAdmission(u)) continue;
          }
        }
        if (u.state === 'admitting' && u.destination && u.bed !== undefined) {
          const bed = u.destination.beds[u.bed];
          if (!this.available(u.destination) || !this.fixedCell(bed.supportCell)) {
            p.hidden = false;
            p.controlled = false;
            p.state = 'incapacitated';
            this.returnUnit(u);
            continue;
          }
          const arrived = this.walk(u, bed, dt, 0.18);
          p.previous.copy(p.p);
          p.p.copy(u.crew.p).add(new THREE.Vector3(0.25, 0.45, 0));
          if (arrived) {
            if (!bed.pose) p.p.set(bed.x, bed.y + 0.5, bed.z);
            p.previous.copy(p.p);
            p.poseTilt = bed.pose ? 0 : undefined;
            p.state = 'recovering';
            p.controlled = true;
            p.layer = `facility:${u.destination.buildingId}`;
            this.care.push({
              person: p,
              phase: bed.pose ? 'entering' : 'resting',
              elapsed: 0,
              from: p.p.clone(),
              yaw: p.yaw,
              tilt: 0,
              unit: u.id,
              facility: u.destination,
              bed: u.bed,
              remaining: 25,
              incident: u.incident!,
            });
            if (!bed.pose) this.admissions++;
            this.reservations.release(u.id, `bed:${u.destination.id}:${u.bed}`);
            this.reservations.acquire(`bed:${u.destination.id}:${u.bed}`, p.id, this.clock, 1, 120);
            this.dispositions.add(p.id);
            this.record(
              u,
              bed.pose ? 'assisting original patient into bed' : 'admitted to occupied hospital bed',
              p.id,
            );
            u.patient = undefined;
            this.returnUnit(u);
            for (const delta of [new THREE.Vector3(-1, 0, 0), new THREE.Vector3(0, 0, -1)]) {
              const goal = u.crew.p.clone().add(delta);
              if (
                this.physics.sweepFoot(u.crew.p, delta) > 0.999 &&
                this.population.footClear(u.crew, goal)
              ) {
                u.handoffExit = goal;
                break;
              }
            }
          }
        }
      }
      if (u.role === 'tow' && u.wreck) {
        const wreck = this.population.cars.find((c) => c.id === u.wreck);
        if (!wreck) continue;
        if (['loading', 'transport', 'unloading'].includes(u.state) && wreck.carrier !== car.id) {
          this.record(u, 'Cargo damaged; original wreck released for reassessment', wreck.id);
          this.returnUnit(u);
          continue;
        }
        this.reservations.acquire(`wreck:${wreck.id}`, u.id, this.clock, 1, 180);
        if (incident?.fires.size) {
          if (incident) incident.reason = 'Tow waiting for fire scene release';
          continue;
        }
        if (u.state === 'approaching' && this.walk(u, this.wreckWorkPoint(wreck, u.crew.p), dt)) {
          u.state = 'hooking';
          u.timer = 4;
          u.crew.state = 'loading';
          this.record(u, 'hooking original wreck', wreck.id);
        }
        if (u.state === 'hooking') {
          u.timer -= dt;
          if (u.timer <= 0) {
            // On-foot responders can reach a wreck long before their truck.
            // Keep the original wreck physical until its actual carrier has
            // stopped alongside it; a crew arrival is not a remote winch.
            if (
              car.state !== 'deployed' ||
              car.actualSpeed > 0.05 ||
              car.p.distanceTo(wreck.p) > (car.length + wreck.length) / 2 + 2.5
            ) {
              u.timer = 0;
              u.crew.state = 'wait';
              const driver = u.additional[0]?.crew;
              if (driver && !driver.fatal && !['incapacitated', 'falling', 'flee'].includes(driver.state))
                u.driverRetained = true;
              if (!u.pickupWaiting) {
                u.pickupWaiting = true;
                this.record(
                  u,
                  'Original wreck waiting for the recovery truck to reach the loading area',
                  wreck.id,
                );
              }
              if (!u.additional.length && !u.crew.hidden) {
                if (this.walk(u, this.crewDoor(u), dt, 0.45)) {
                  u.crew.hidden = true;
                  u.crew.state = 'service';
                }
              }
              this.relocateRendezvous(u, wreck);
              continue;
            }
            u.pickupWaiting = false;
            const depot = (this.city.facilities ?? []).find((f) => f.kind === 'depot' && this.available(f));
            if (depot) {
              u.destination = depot;
              wreck.state = 'towed';
              wreck.carrier = car.id;
              wreck.controlled = true;
              wreck.body.setBodyType(RAPIER.RigidBodyType.KinematicPositionBased, true);
              wreck.collider.setCollisionGroups(0);
              u.loadFrom = wreck.p.clone();
              u.loadTo = car.p.clone().add(new THREE.Vector3(0, 1.25, 1.3).applyQuaternion(car.q));
              u.state = 'loading';
              u.timer = 4;
            }
          }
        }
        if (u.state === 'loading') {
          u.timer = Math.max(0, u.timer - dt);
          wreck.previous.copy(wreck.p);
          wreck.p.copy(u.loadFrom!).lerp(u.loadTo!, 1 - u.timer / 4);
          wreck.body.setNextKinematicTranslation(wreck.p);
          const door = this.crewDoor(u);
          const aboard = this.walk(u, door, dt);
          if (u.timer === 0 && aboard && u.destination) {
            u.crew.hidden = true;
            const depot = u.destination,
              b = this.city.buildings.find((b) => b.id === depot.buildingId)!,
              yard = { x: depot.bays[1].x, y: 0.16, z: b.z + b.d * 1.25 + 5 };
            if (this.routeVehicle(u, yard, depot)) {
              u.state = 'transport';
              this.record(u, 'loaded original wreck', wreck.id);
            }
          }
        }
        if (u.state === 'transport') {
          wreck.previous.copy(wreck.p);
          wreck.previousQ.copy(wreck.q);
          wreck.p.copy(car.p).add(new THREE.Vector3(0, 1.25, 1.3).applyQuaternion(car.q));
          wreck.q.copy(car.q);
          wreck.body.setNextKinematicTranslation(wreck.p);
          wreck.body.setNextKinematicRotation(wreck.q);
          if (car.state === 'deployed' && u.destination) {
            u.loadFrom = wreck.p.clone();
            u.loadTo = car.p
              .clone()
              .add(new THREE.Vector3(0, 0, car.length / 2 + wreck.length / 2 + 1.25).applyQuaternion(car.q));
            u.loadTo.y = this.physics.ground(u.loadTo, 0.35, 0.5)?.y ?? 0.16;
            u.state = 'unloading';
            u.timer = 4;
          }
        }
        if (u.state === 'unloading') {
          u.timer = Math.max(0, u.timer - dt);
          wreck.previous.copy(wreck.p);
          wreck.p.copy(u.loadFrom!).lerp(u.loadTo!, 1 - u.timer / 4);
          wreck.body.setNextKinematicTranslation(wreck.p);
          if (u.timer === 0) {
            wreck.body.setTranslation(wreck.p, true);
            wreck.collider.setCollisionGroups(0xffffffff);
            wreck.body.setBodyType(RAPIER.RigidBodyType.Dynamic, true);
            wreck.body.setLinvel({ x: 0, y: 0, z: 0 }, true);
            wreck.state = 'impounded';
            wreck.carrier = undefined;
            this.dispositions.add(wreck.id);
            this.towed++;
            this.record(u, 'original wreck deposited at service depot', wreck.id);
            u.wreck = undefined;
            this.returnUnit(u);
          }
        }
      }
    }
    for (const u of this.units) this.updateTeam(u, dt);
    for (let k = this.care.length - 1; k >= 0; k--) {
      const care = this.care[k],
        p = care.person;
      const bed = care.facility.beds[care.bed];
      if (
        p.fatal ||
        p.state !== 'recovering' ||
        !this.available(care.facility) ||
        !this.fixedCell(bed.supportCell) ||
        (bed.furnitureId !== undefined && !this.fixedCell(bed.furnitureId * 1e6))
      ) {
        p.poseTilt = p.seatHeight = p.seatBlend = undefined;
        p.state = 'incapacitated';
        p.controlled = false;
        this.reservations.release(p.id);
        this.dispositions.delete(p.id);
        this.care.splice(k, 1);
        continue;
      }
      this.reservations.acquire(`bed:${care.facility.id}:${care.bed}`, p.id, this.clock, 1, 120);
      if (care.phase === 'entering') {
        if (this.carePose(care, dt)) {
          care.phase = 'resting';
          this.admissions++;
          this.log.push({
            time: this.clock,
            incident: care.incident,
            unit: care.unit,
            action: 'admitted to occupied hospital bed',
            entity: p.id,
          });
        }
        continue;
      }
      if (care.phase === 'resting') {
        care.remaining -= dt;
        if (care.remaining > 0) continue;
        care.phase = 'leaving';
        care.elapsed = 0;
        care.from.copy(p.p);
        care.yaw = p.yaw;
        care.tilt = p.poseTilt ?? 0;
      }
      if (care.phase === 'leaving' && this.carePose(care, dt)) {
        p.poseTilt = p.seatHeight = p.seatBlend = undefined;
        this.reservations.release(p.id);
        p.controlled = false;
        p.hidden = false;
        p.state = 'walk';
        p.p.y = care.facility.beds[care.bed].y;
        p.previous.copy(p.p);
        p.timer = 2;
        this.population.civil?.resume(p, `facility:${care.facility.buildingId}`);
        this.recovered++;
        this.care.splice(k, 1);
      }
    }
  }
  private wreckWorkPoint(wreck: TrafficVehicle, crew: Vec): THREE.Vector3 {
    const options = [-1, 1].map((side) =>
      wreck.p.clone().add(new THREE.Vector3(side * (wreck.width / 2 + 0.65), 0, 0).applyQuaternion(wreck.q)),
    );
    const supported = options.flatMap((point) => {
      const support = this.physics.ground(point, 0.35, 1.8);
      if (
        !support ||
        Math.abs(support.y - wreck.p.y) > 1.8 ||
        this.physics.blocked(point.x, point.z, 0.26, support.y + 0.85, wreck.body)
      )
        return [];
      point.y = support.y;
      return [point];
    });
    return (supported.length ? supported : options).sort(
      (a, b) => Math.hypot(a.x - crew.x, a.z - crew.z) - Math.hypot(b.x - crew.x, b.z - crew.z),
    )[0];
  }
  private crewDoor(u: Unit): THREE.Vector3 {
    return (
      u.unloadDoor ??
      u.car.p.clone().add(new THREE.Vector3(u.car.width / 2 + 0.7, 0, 0).applyQuaternion(u.car.q))
    );
  }
  private stationDuty(u: Unit, dt: number): void {
    const place = u.duty;
    if (!place) return;
    const crew = u.crew;
    if (['incapacitated', 'falling', 'flee'].includes(crew.state)) return;
    if (!this.population.civil?.network.available(place)) {
      crew.state = 'service';
      return;
    }
    if (crew.hidden) {
      if (!this.unloadCrew(u)) return;
      crew.layer = `facility:${u.facility.buildingId}`;
      u.dutyStarted = false;
    }
    const target = u.dutyStep === 0 ? place : this.crewDoor(u);
    if (!u.dutyStarted) {
      this.crewRoute(u, target);
      u.dutyStarted = true;
      u.timer = u.dutyStep === 0 ? place.duration : 6;
    }
    const support = this.physics.ground(crew.p, 0.28, 0.4);
    if (!support) {
      crew.state = 'falling';
      crew.fallStart = crew.p.y;
      crew.velocity.set(0, 0, 0);
      return;
    }
    if (!this.walk(u, target, dt, 0.25)) return;
    crew.goal.set(target.x, target.y, target.z);
    crew.state = u.dutyStep === 0 ? 'inspecting equipment' : 'checking vehicle';
    crew.yaw = u.dutyStep === 0 ? (place.facing ?? crew.yaw) : u.car.yaw + Math.PI / 2;
    if (u.role === 'fire' && u.dutyStep === 0) u.water = Math.min(1, u.water + dt / 8);
    u.timer -= dt;
    if (u.timer <= 0) {
      u.dutyChecks++;
      this.record(u, u.dutyStep === 0 ? 'equipment check complete' : 'vehicle check complete');
      u.dutyStep = 1 - u.dutyStep;
      u.dutyStarted = false;
    }
  }
  private carePose(care: Care, dt: number): boolean {
    const bed = care.facility.beds[care.bed],
      pose = bed.pose,
      p = care.person;
    if (!pose) return true;
    const entering = care.phase === 'entering',
      elapsed = Math.min(pose.duration, care.elapsed + dt),
      t = elapsed / pose.duration,
      end = entering
        ? new THREE.Vector3(pose.x, pose.y + pose.seatHeight, pose.z)
        : new THREE.Vector3(bed.x, bed.y, bed.z),
      next = care.from.clone().lerp(end, t),
      yaw = entering ? pose.yaw : 0,
      nextYaw = care.yaw + Math.atan2(Math.sin(yaw - care.yaw), Math.cos(yaw - care.yaw)) * t,
      tilt = entering ? (-Math.PI / 2) * t : care.tilt * (1 - t);
    if (
      !this.population.footClear(p, next) ||
      !this.physics.interactionClear(
        p.p,
        next,
        p.yaw,
        nextYaw,
        p.poseTilt ?? 0,
        tilt,
        p.scale,
        bed.furnitureId,
      )
    )
      return false;
    care.elapsed = elapsed;
    p.previous.copy(p.p);
    p.p.copy(next);
    p.yaw = nextYaw;
    p.poseTilt = tilt;
    p.seatHeight = pose.seatHeight;
    p.seatBlend = entering ? t : 1 - t;
    return t >= 1;
  }
  render(): void {
    let count = 0;
    for (const u of this.units)
      if (u.state === 'suppression') {
        const i = this.incidents.get(u.incident!);
        if (i)
          for (const p of [u.crew, ...u.additional.map((m) => m.crew)]) {
            if (p.hidden || p.state !== 'suppressing') continue;
            this.hoseData.set([p.p.x, p.p.y + 1, p.p.z, i.point.x, i.point.y + 1, i.point.z], count * 6);
            count++;
          }
      }
    this.hose.geometry.setDrawRange(0, count * 2);
    const position = this.hose.geometry.getAttribute('position') as THREE.BufferAttribute;
    position.clearUpdateRanges();
    if (count) position.addUpdateRange(0, count * 6);
    position.needsUpdate = true;
  }
  inspect(): unknown {
    return {
      assistance: [...this.assistance.jobs.values()].map((job) => {
        const path = this.assistingPaths.get(job.crew.id);
        return {
          crew: job.crew.id,
          work: job.key,
          position: job.crew.p.toArray(),
          state: job.crew.state,
          goal: job.goal.toArray(),
          approach: job.approach?.toArray(),
          started: job.started,
          progress: job.progress,
          clusterPosition: job.cluster?.body.translation(),
          planning: !!path?.planning,
          failure: path?.planFailure,
          next:
            path?.footBypass?.[0] ??
            (path?.route[path.cursor] === undefined
              ? path?.directGoal
              : this.population.civil?.network.nodes[path.route[path.cursor]]),
        };
      }),
      facilities: (this.city.facilities ?? []).map((f) => ({ ...f, available: this.available(f) })),
      incidents: [...this.incidents.values()].map((i) => ({
        ...i,
        point: i.point.toArray(),
        fires: [...i.fires],
        people: [...i.people],
        wrecks: [...i.wrecks],
      })),
      units: this.units.map((u) => ({
        id: u.id,
        role: u.role,
        state: u.state,
        incident: u.incident,
        patient: u.patient,
        wreck: u.wreck,
        water: u.water,
        dutyChecks: u.dutyChecks,
        duty: u.duty?.name,
        crewActivity: u.crew.state,
        crewCount: 1 + u.additional.length,
        footFallback: !!u.footFallback,
        airHandoffRequested: !!u.airHandoffRequested,
        retainedDriver: u.driverRetained ? u.additional[0]?.crew.id : undefined,
        rendezvousMoving: !!u.rendezvousMoving,
        footAccessWaitingSeconds: u.accessSince === undefined ? 0 : this.clock - u.accessSince,
        members: [u.crew, ...u.additional.map((m) => m.crew)].map((p) => ({
          id: p.id,
          activity: p.state,
          aboard: p.hidden,
          position: p.p.toArray(),
          work: u.additional.find((m) => m.crew === p)?.work ?? 0,
        })),
        blocked: u.car.blockedBy,
        position: u.car.p.toArray(),
        route: u.car.plan.slice(0, 8),
        finalGoal: u.routeGoal,
        speed: u.car.actualSpeed,
        crew: {
          position: u.crew.p.toArray(),
          destination: u.routePoint?.toArray(),
          planning: !!u.planning,
          planFailure: u.planFailure,
          finalApproach: u.footExit,
          route: u.route.slice(u.cursor, u.cursor + 5).map((id) => this.population.civil?.network.nodes[id]),
          detour: u.crew.detour?.toArray(),
        },
      })),
      log: this.log,
    };
  }
  snapshot(): Record<string, number> {
    return {
      serviceUnits: this.units.length,
      serviceCrew: this.units.reduce((n, u) => n + 1 + u.additional.length, 0),
      serviceCrewAboard: this.units.reduce(
        (n, u) => n + Number(u.crew.hidden) + u.additional.filter((m) => m.crew.hidden).length,
        0,
      ),
      serviceFootFallbacks: this.units.filter((u) => u.footFallback).length,
      fieldFirstAid: this.assistance.aided,
      rubbleClearedToVerge: this.assistance.cleared,
      rubblePushedMetres: this.assistance.pushedMetres,
      auxiliaryWorkAssignments: this.assistance.jobs.size,
      serviceRoutePlans: this.crewPlans.size,
      serviceAirAccessRequests: [...this.airAccess.values()].filter((until) => until > this.clock).length,
      availableServiceUnits: this.units.filter((u) => u.state === 'ready').length,
      sharedIncidents: [...this.incidents.values()].filter((i) => i.state !== 'complete').length,
      hospitalAdmissions: this.admissions,
      patientsRecovered: this.recovered,
      wrecksTowed: this.towed,
      serviceWater: this.waterApplied,
      hospitalOccupiedBeds: this.care.length,
    };
  }
  dispose(): void {
    this.clear();
    this.physics.scene.remove(this.hose);
    this.hose.geometry.dispose();
    this.hose.material.dispose();
  }
}
